import AccountPanel from "./AccountPanel";
import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import "@/MiniApp.css";
import IntelligenceDetail, { authorityMark } from "@/IntelligenceDetail";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { getRoadShield, roadLine } from "./roadLine";
import { latestOnly } from "./latestOnly";
// Eén leeftijdsregel voor de hele app, en dezelfde afronding als de
// server. Zie leeftijd.js: hier stond een eigen versie die dagen afkapte,
// waardoor dezelfde melding "1 dag" op het kaartje en "2 dagen" op het
// detailscherm was.
import { timeAgo, vrijgegevenLabel } from "./leeftijd";
import {
  visibleReports, countFor, typeChoices, groupByType, pinsOfTypes, toggleType,
  allTypes, isDefault, TONE_RANK,
  WHERE_ROAD, WHERE_ALL, DEFAULT_FILTER,
} from "./boardFilter";
import { FALLBACK_TYPES } from "./fallbackTypes";
import { group } from "./feedBuckets";
import { glyphFor, glyphShape, markerHtml } from "./markGlyph";
import { nieuweFix, verdientAnker, opFix, huidigeFix } from "./driveFix";
import { moetOphalen, TEMPO, KAART_STRAAL_M } from "./ophaalTempo";
import { lijnUitRit } from "./polylijn";
import { magZoeken, RUST_MS } from "./ritTempo";
import { ritTitel, ritOnder, ritKm } from "./ritRegel";
import { effectieveLimiet, limietWijktAf } from "./voertuig";
import {
  TERUGVAL as VOERTUIG_TERUGVAL, STANDAARD as VOERTUIG_STANDAARD,
  gaatMijAan, soortGaatMijAan, profielVan,
  lees as leesVoertuig, schrijf as schrijfVoertuig,
  leesExtra as leesVoertuigExtra, schrijfExtra as schrijfVoertuigExtra,
} from "./voertuig";
import {
  volgTraject, averageKmh, remainingM, allowedRestKmh, isOver, travelledM,
} from "./sectionRun";
import { syncAge } from "./syncAge";
import { leeslast, indexRegel, samenstelling, vergelijking } from "./leeslast";
import { feedMark, feedTone, feedUrgent } from "./feedLook";
import { compassName, opposite } from "./compass";
import { dagMaand } from "./maanden";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// ============================================================
// De ondergrond
// ============================================================
// Carto's gratis `dark_all` stempelt sinds kort "API KEY REQUIRED" dwars over
// elke tegel. Dat is bij hen veranderd en niet bij ons — nagekeken door één
// tegel op te halen en ernaar te kijken: 2,3 kB waarvan de helft de
// watermerktekst is.
//
// Esri's Dark Gray Canvas vraagt geen sleutel, is net zo donker en dekt
// Nederland tot in de straten. Base en Reference zijn twee lagen: de eerste
// tekent de wereld, de tweede de namen erbovenop. Dat is hoe Esri ze bedoelt.
//
// Eén plek, want vier losse URL's in één bestand betekent dat de volgende
// verhuizing er drie vergeet.
const TEGELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const TEGELS_NAMEN = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const TEGELS_BRON = "Esri, HERE, Garmin, &copy; OpenStreetMap";

// ============================================================
// CONFIG CONTEXT - Fetches from backend API
// ============================================================
const ConfigContext = createContext(null);

const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

// Fallback config in case API is unavailable
// What the Melden screen offers when the server has not answered yet, or at
// all. Kept in step with backend/config.py by hand.
//
// Without it the screen was blank on a failed or slow config request, which is
// worse than a stale list: somebody who opens Melden to report an accident
// gets nothing to tap and no reason why. `GET /api/config/all` remains the
// source of truth and replaces this the moment it lands.
const FALLBACK_AUTHORITIES = [
  { id: "politie", emoji: "🚔", name: "Politie", category: "authority" },
  { id: "ilt", emoji: "🚛", name: "ILT", category: "authority" },
  { id: "belasting", emoji: "💰", name: "Belastingdienst", category: "authority" },
  { id: "douane", emoji: "🛂", name: "Douane", category: "authority" },
  { id: "marechaussee", emoji: "🎖️", name: "Marechaussee", category: "authority" },
  { id: "taxiteam", emoji: "🚕", name: "Taxiteam", category: "authority" },
];

// The three blocks on the Melden screen, in order. "Blijft staan" rather than
// "Infrastructuur": it says what it means for the person reporting instead of
// which drawer it lives in.
const TYPE_GROUPS = [
  { key: "controle", title: "Controles" },
  { key: "situatie", title: "Situaties" },
  { key: "blijvend", title: "Blijft staan" },
];

const TYPE_BY_ID = (types, id) => types.find(t => t.id === id);

const FALLBACK_CONTROL_TYPES = [
  { id: "snelheid", emoji: "📸", name: "Snelheid" },
  { id: "alcohol", emoji: "🍺", name: "Alcohol" },
  { id: "drugs", emoji: "💊", name: "Drugs" },
  { id: "telefoon", emoji: "📱", name: "Telefoon" },
  { id: "inspectie", emoji: "🔍", name: "Inspectie" },
  { id: "algemeen", emoji: "🔍", name: "Algemeen" },
];

const ConfigProvider = ({ children }) => {
  const [authorities, setAuthorities] = useState(FALLBACK_AUTHORITIES);
  const [controlTypes, setControlTypes] = useState(FALLBACK_CONTROL_TYPES);
  // Every report type, in the order the server offers them, each carrying its
  // service, its colour and which of the three blocks it belongs in. One list
  // instead of "authorities crossed with control types" — see config.py.
  const [types, setTypes] = useState(FALLBACK_TYPES);
  // De voertuigprofielen komen van de server, want ze staan ook in Android en
  // iOS en drie kopieën lopen uit elkaar. VOERTUIG_TERUGVAL is de ingebouwde
  // kopie voor de koude start, met dezelfde plicht tot handmatig gelijkhouden
  // als FALLBACK_TYPES.
  const [profielen, setProfielen] = useState(VOERTUIG_TERUGVAL);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/all`);
        if (response.data) {
          const all = response.data.types || response.data.authorities || [];
          // An empty answer is not an answer: keep the fallback rather than
          // blank the report screen.
          setTypes(all.length > 0 ? all : FALLBACK_TYPES);
          // Kept for the Watch tab, which filters on the services behind the
          // types rather than on the types themselves.
          const mainAuths = all.filter(a => a.category === 'authority');
          setAuthorities(mainAuths.length > 0 ? mainAuths : FALLBACK_AUTHORITIES);
          setControlTypes(response.data.control_types || FALLBACK_CONTROL_TYPES);
          const prof = response.data.profielen;
          setProfielen(Array.isArray(prof) && prof.length ? prof : VOERTUIG_TERUGVAL);
        }
        setConfigLoaded(true);
      } catch (error) {
        console.warn('Failed to fetch config, using fallback:', error);
        setConfigLoaded(true);
      }
    };
    fetchConfig();
  }, []);

  // Build lookup maps for quick access
  const authorityMap = useMemo(() => {
    const map = {};
    authorities.forEach(a => { map[a.id] = a; });
    return map;
  }, [authorities]);

  const controlTypeMap = useMemo(() => {
    const map = {};
    controlTypes.forEach(c => { map[c.id] = c; });
    return map;
  }, [controlTypes]);

  // LET OP, en dit heeft op 9 oktober een scherm vol "Onbekend" gekost.
  //
  // Dit lijkt de manier om de naam bij een `melding.authority` te vinden, en
  // dat is het niet. `authorities` is hierboven gefilterd op
  // `category === 'authority'`: 12 van de 23 types. De elf die wegvallen zijn
  // alle flitsers, alle trajectcontroles, alle wegwerkzaamheden en alle
  // situaties — dus zo ongeveer elke rij die op een rit of op het bord staat.
  // Voor die elf geeft deze functie "Onbekend", zonder fout en zonder lege
  // plek: een geloofwaardig verkeerd woord.
  //
  // Wil je de naam bij een melding? Gebruik `types` en `ritTitel()` uit
  // ritRegel.js, of TYPE_BY_ID. Deze functie en getControlType hebben op dit
  // moment geen enkele aanroeper (nagekeken op 9 oktober, recursief over src/);
  // ze staan er voor een Watch-tab die op diensten filtert en die lijst nu van
  // de server krijgt.
  const getAuthority = useCallback((id) => authorityMap[id] || { emoji: "❓", name: "Onbekend" }, [authorityMap]);
  const getControlType = useCallback((id) => controlTypeMap[id] || { emoji: "❓", name: "Onbekend" }, [controlTypeMap]);

  return (
    <ConfigContext.Provider value={{
      authorities,
      controlTypes,
      types,
      profielen,
      configLoaded,
      getAuthority,
      getControlType,
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Helper function to shorten addresses to "Street, City" format
const shortenAddress = (address, city) => {
  if (!address) return city || "Onbekend";
  if (address.startsWith("Nabij ")) return address;
  
  const parts = address.split(",").map(p => p.trim());
  if (!parts.length) return city || "Onbekend";
  
  let street = parts[0];
  
  // If first part is just a number (house number), combine with second part
  if (/^\d+(-?\w*)?$/.test(street) && parts.length > 1) {
    // First part is a house number like "9" or "73-H", combine with street name
    street = `${parts[1]} ${street}`;
  }
  
  // Capitalize first letter if needed
  if (street && street[0] === street[0].toLowerCase()) {
    street = street[0].toUpperCase() + street.slice(1);
  }
  
  // If street is same as city, return "Nabij city"
  if (city && street.toLowerCase() === city.toLowerCase()) {
    return `Nabij ${city}`;
  }
  
  return city ? `${street}, ${city}` : street;
};

// Custom marker for user location
const userLocationIcon = L.divIcon({
  className: "user-marker",
  html: `<div style="
    width: 20px;
    height: 20px;
    background: #3B82F6;
    border: 4px solid white;
    border-radius: 50%;
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Create marker icon based on authority
// Which service, as a colour. Same values as the --svc-* custom properties, so
// a dot on the map and the mark on its card are recognisably the same thing.
// ------------------------------------------------------------
// Kleur betekent precies één ding: welke dienst.
// ------------------------------------------------------------
// Van de weg: "wegwerkzaamheden ineens blauw en controle geel." Nagemeten, en
// de oorzaak lag een laag dieper dan een verkeerde tint — kleur droeg twee
// betekenissen tegelijk. Welke dienst het is (blauw = politie, geel = werk) en
// in welke staat iets verkeert (blauw = officiële data, amber = let op, groen =
// bevestigd). Die twee paletten overlapten:
//
//     situatie #fbbf24  ==  "let op"  #fbbf24   — exact dezelfde waarde
//     taxi     #eab308  ~=  situatie            — 5 eenheden uit elkaar
//     ilt      #4ade80  ~=  "bevestigd"         — 11
//     politie  #5b93f5  ~=  "officieel"         — 19, en die tint kleurde de
//                                                 hele rij van een officieel
//                                                 item: vandaar dat blauw.
//
// (Afstanden in CIE76 over Lab; onder ~20 zijn twee kleuren op een telefoon in
// een auto niet uit elkaar te houden.)
//
// Zoeken naar een palet waarin veertien betekenissen allemaal ver genoeg uit
// elkaar liggen levert niets op — dat past niet in één kanaal. Dus is de
// oplossing niet chromatisch maar structureel: de staat gebruikt geen kleur
// meer als vulling of rand, en de drie dienstkleuren die op een toestandskleur
// leken zijn verplaatst.
//
// Wat overblijft is 13,1 tussen het geel van werk en de amber van "let op". Dat
// blijft staan en het is een keuze: de eerste is een stip, de tweede is tekst,
// ze zijn nooit hetzelfde element. Wat wél dodelijk was — stip tegen stip, en
// een vulling die een dienstkleur nabootste — is weg.
const SERVICE_COLOUR = {
  pol: "#5b93f5",
  // In de groene familie waar ILT thuishoort, weg van het groen van "bevestigd".
  ilt: "#84cc16",
  // Had geen natuurlijke kleur en stond op geel, naast twee andere gelen.
  taxi: "#22d3ee",
  dou: "#c084fc",
  kmar: "#93a4e0",
  bel: "#7fb6c6",
  flits: "#fca5a5",
  werk: "#fcd34d",
  // Situations are not a service — nobody is checking, something happened.
  // Oranje, de kleur die daar overal voor staat, en ver van het geel van werk
  // en van de amber die "let op" betekent. Stond op precies die amber.
  sit: "#f97316",
};

const serviceColour = (authority, name) =>
  SERVICE_COLOUR[authorityMark(authority, name).tone] || SERVICE_COLOUR.pol;

// Een teken in de kleur van zijn dienst.
//
// Dit was eerst een emoji-ballon en daarna een gekleurde stip. De ballon dekte
// de stad die hij moest beschrijven; de stip vraagt je de kleurcode te kennen
// voordat hij iets zegt. Een klein monochroom teken in een donkere ring zegt
// het zelf — en blijft klein genoeg dat de kaart het hoofdding blijft.
//
// De kleur zegt wélke dienst, het teken zegt wát het is. Die twee zijn niet
// hetzelfde: `werk` is één kleur voor wegwerkzaamheden, een dichte rijstrook,
// een afgesloten weg en een open brug. Zie markGlyph.js.
const serviceDot = (melding, { selected = false, faded = false, compact = false,
                               plain = false } = {}) => {
  // Drie maten, en de kleinste is geen bezuiniging: het kaartje boven het bord
  // is 178 px hoog en tekent er veertig. Zie de opmerking bij FeedMap.
  const size = selected ? 30 : compact ? 17 : plain ? 11 : 23;
  const colour = faded ? "#6B7280" : serviceColour(melding.authority, melding.authority_name);
  const { tone } = authorityMark(melding.authority, melding.authority_name);
  return L.divIcon({
    className: "svc-dot",
    // Uitgezoomd: een stip, en één DOM-knoop in plaats van tien. Zie
    // ZoomWatcher voor de meting die dit afdwong.
    html: plain
      ? `<span style="
          display:block; width:${size}px; height:${size}px; border-radius:50%;
          background:${colour}; border:2px solid rgba(5,8,11,.75);
          opacity:${faded ? 0.55 : 1};
        "></span>`
      : markerHtml({
          shape: glyphShape(glyphFor(melding.authority, tone)),
          colour,
          size,
          faded,
        }),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

/**
 * Waar een trajectcontrole begint en eindigt.
 *
 * Een traject werd getekend als een dikke rode lijn van 5 px. Dat leest als een
 * alarm over de hele lengte, terwijl het tegenovergestelde waar is: tussen de
 * twee palen gebeurt niets, het gaat om je gemiddelde. De lijn schreeuwde dus
 * precies waar er niets te zien valt. Gemeld als: vergeet die rode lijn.
 *
 * Nu een dunne rail in de camerakleur — hij zegt "je wordt hier gemeten", niet
 * "pas op" — met een ring op beide uiteinden, want dáár staan de twee palen.
 * Een ring en geen stip, zodat hij niet te verwarren is met een melding.
 */
// De twee uiteinden van een rit.
//
// Op moduleniveau en niet in de component, om dezelfde reden als
// userLocationIcon en sectionEndIcon: een L.divIcon die bij elke hertekening
// opnieuw gemaakt wordt, laat Leaflet de marker-DOM opnieuw opbouwen.
//
// Eigen vorm en niet sectionEndIcon hergebruiken: die ring betekent in deze app
// "trajectpaal", en twee dingen die hetzelfde betekenen op één kaart is erger
// dan een icoon erbij. Open voor het vertrek, dicht voor de bestemming — de
// richting is dan uit het plaatje te lezen zonder legenda.
const ritStartIcon = L.divIcon({
  className: "rit-punt",
  html: `<span style="
    display:block; width:11px; height:11px; border-radius:50%;
    border:2px solid #2f6fe4; background:rgba(5,8,11,.65);
  "></span>`,
  iconSize: [11, 11],
  iconAnchor: [5.5, 5.5],
});

const ritEindIcon = L.divIcon({
  className: "rit-punt",
  html: `<span style="
    display:block; width:11px; height:11px; border-radius:50%;
    border:2px solid #2f6fe4; background:#2f6fe4;
  "></span>`,
  iconSize: [11, 11],
  iconAnchor: [5.5, 5.5],
});

const sectionEndIcon = L.divIcon({
  className: "sec-end",
  html: `<span style="
    display:block; width:9px; height:9px; border-radius:50%;
    border:2px solid #fca5a5; background:rgba(5,8,11,.55);
  "></span>`,
  iconSize: [9, 9],
  iconAnchor: [4.5, 4.5],
});

// Een patroon is context, geen alarm. Daarom een rustige, vaste marker zonder
// vuur, score-afhankelijke grootte of pulserende animatie.
const createPatternIcon = (relevantNow = false) => {
  const size = relevantNow ? 34 : 30;
  return L.divIcon({
    className: `pattern-marker${relevantNow ? " relevant" : ""}`,
    html: `<span class="pattern-marker-core" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M5 8h3l1.4-2h5.2L16 8h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"/>
        <circle cx="12" cy="13" r="3"/>
      </svg>
    </span>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

// Authority types - controls (temporary)
// These are now fallbacks - real config comes from ConfigContext
const AUTHORITIES = FALLBACK_AUTHORITIES;

// Situations (temporary)
const SITUATIONS = [
  { id: "gevaar", emoji: "⚠️", name: "Gevaar", category: "situation" },
  { id: "kapot_voertuig", emoji: "🚗", name: "Kapot voertuig", category: "situation" },
  { id: "object_op_weg", emoji: "🚧", name: "Object op weg", category: "situation" },
];

// Infrastructure (persistent)
const INFRASTRUCTURE = [
  { id: "mobiele_flitser", emoji: "📷", name: "Mobiele flitser", category: "infrastructure" },
  { id: "telefoonflitser", emoji: "📱", name: "Telefoonflitser", category: "infrastructure" },
  { id: "wegafsluiting", emoji: "🚫", name: "Wegafsluiting", category: "infrastructure" },
  { id: "wegwerkzaamheden", emoji: "🔧", name: "Wegwerkzaamheden", category: "infrastructure" },
];

// Control types - now fallback, real comes from ConfigContext
const CONTROL_TYPES = FALLBACK_CONTROL_TYPES;

// Radius options for Watch
const RADIUS_OPTIONS = [
  { value: 1000, label: "1 km" },
  { value: 3000, label: "3 km" },
  { value: 5000, label: "5 km" },
  { value: 10000, label: "10 km" },
];

// Time ago helper

// Past this, a report is no longer news. The age keeps its place on the card
// but loses its colour — the mockup's .age.stale. Fifteen minutes is roughly
// how long a checkpoint stays worth reacting to at motorway speed.
const STALE_AFTER_MINUTES = 15;

const isStale = (timestamp) => {
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return false;
  return (Date.now() - then) / 60000 > STALE_AFTER_MINUTES;
};

// Three dots, filled to match the backend's own confidence level. Deliberately
// derived from that one value rather than recounted here: two screens
// disagreeing about how sure we are is worse than either being wrong.
const confidenceDots = (level) => (level === "high" ? 3 : level === "medium" ? 2 : 1);

// When road works end, as its own line on the card. NDW gives a real end date;
// until now it only ever reached a driver stuck onto the address string
// ("Gooiseweg · t/m 14-08"), where it read as part of the street name.
const untilLabel = (endIso) => {
  if (!endIso) return null;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  if (end < now) return null;
  const date = dagMaand(end);
  const days = Math.ceil((end - now) / 86400000);
  return days <= 14 ? `Nog ${days} ${days === 1 ? "dag" : "dagen"} · t/m ${date}` : `T/m ${date}`;
};

// What a board row is called. The service mark sits right next to it, so the
// name of the service is the one thing the title should not carry. When the
// control type says nothing ("Algemeen") the service name is all there is, and
// then it earns its place.
const GENERIC_CONTROL = new Set(["Algemeen", "Onbekend", ""]);

const cardTitle = (m) => {
  const control = m.control_name || "";
  if (m.authorities && m.authorities.length > 1) {
    return GENERIC_CONTROL.has(control)
      ? m.authorities_display
      : `${m.authorities_display} · ${control}`;
  }
  return GENERIC_CONTROL.has(control) ? m.authority_name : control;
};

// How the board orders itself unless you say otherwise.
const DEFAULT_SORT = "proximity";

// The three orderings, in the order they are offered. A list, so the menu and
// the sorting cannot drift apart — they used to be three hand-written buttons
// beside a switch statement.
const SORT_OPTIONS = [
  { id: "proximity", label: "Dichtbij", needsLocation: true },
  { id: "newest", label: "Nieuwste" },
  { id: "confirmations", label: "Meest bevestigd" },
];

// Confirmation tier helper — maps confirmations to Live Radar tier (colour on the count only)
const getTier = (confirmations = 0) => {
  if (confirmations >= 10) return "hot";
  if (confirmations >= 3) return "active";
  return "ver";
};

/* "via OpenStreetMap" used to sit under every official card. Where the data
   comes from is a fact about our plumbing, not something a driver needs at
   100 km/h, and its removal was asked for. The badge already says the item is
   official.

   The attribution itself stays: ODbL requires it, and a licence belongs in
   Instellingen → Bronnen, stated once, where it can name the licence properly
   instead of three words on a card. */
// Map center component
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom(), { animate: true, duration: 0.5 });
    }
  }, [center, zoom, map]);
  return null;
};

/**
 * Op welk niveau de kaart staat, zodat de spelden zich kunnen aanpassen.
 *
 * Ik bouwde dit voor de snelheid en dat bleek de verkeerde reden. Gemeten op
 * deze kaart, met de 1858 spelden die de server landelijk teruggeeft:
 *
 *     stippen   3.804 knopen, 0 paden      868 ms
 *     tekens   11.887 knopen, 6.226 paden  867 ms
 *
 * Precies even snel. Het knelpunt is Leaflet dat 1858 markers plaatst, niet wat
 * erin zit — het aantal knopen verdrievoudigt en de tijd beweegt niet.
 *
 * Het blijft staan om de reden die wél klopt: een teken van 23 px is op een
 * kaart waar heel Nederland op past niet te lezen. Daar is de vraag "waar zit
 * het", en dat beantwoordt een stip beter. Dat het uitgezoomd ook achtduizend
 * knopen scheelt, is meegenomen en niet de aanleiding.
 */
const ZoomWatcher = ({ onZoom }) => {
  const map = useMap();
  useEffect(() => {
    const meld = () => onZoom(map.getZoom());
    meld();
    map.on("zoomend", meld);
    return () => map.off("zoomend", meld);
  }, [map, onZoom]);
  return null;
};

/**
 * Vanaf welk niveau een teken leesbaar is.
 *
 * Elf is ongeveer "een stad vult het scherm". Daaronder staan de spelden zo
 * dicht op elkaar dat de vorm erin toch verdwijnt.
 */
const GLYPH_VANAF_ZOOM = 11;

// Fly-to component triggered by counter
const MapFlyTo = ({ center, trigger }) => {
  const map = useMap();
  useEffect(() => {
    if (trigger > 0 && center) {
      map.flyTo(center, 16, { duration: 0.8 });
    }
  }, [trigger, center, map]);
  return null;
};

/**
 * De kaart om een hele lijn heen leggen.
 *
 * Het enige echt nieuwe stuk kaartcode in dit bestand: alle vier bestaande
 * kaarten zetten een center plus een zoom, en een route heeft geen center —
 * die heeft twee uiteinden die beide in beeld moeten.
 *
 * Als useMap-kind en niet als prop op MapContainer, want `fitBounds` heeft de
 * maat van de kaart nodig en die bestaat pas als de kaart er is. En onder de
 * twee punten gebeurt er niets: `fitBounds` op minder gooit "Bounds are not
 * valid", en er staat geen error boundary in dit bestand — dat zou dus niet de
 * kaart maar de hele app kosten.
 */
const KaartOpLijn = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    map.fitBounds(positions, { padding: [26, 26], animate: false });
  }, [positions, map]);
  return null;
};

// Skeleton loading component
const SkeletonCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-header">
      <div className="skeleton-icon"></div>
      <div className="skeleton-text-group">
        <div className="skeleton-text skeleton-title"></div>
        <div className="skeleton-text skeleton-subtitle"></div>
        <div className="skeleton-text skeleton-small"></div>
      </div>
    </div>
    <div className="skeleton-stats">
      <div className="skeleton-text skeleton-stat"></div>
      <div className="skeleton-text skeleton-stat"></div>
    </div>
    <div className="skeleton-actions">
      <div className="skeleton-btn"></div>
      <div className="skeleton-btn"></div>
      <div className="skeleton-btn"></div>
      <div className="skeleton-btn"></div>
    </div>
  </div>
);

// Map loading spinner
const MapSpinner = () => (
  <div className="map-loading">
    <div className="spinner"></div>
    <span>Kaart laden...</span>
  </div>
);

// ============================================================
// MELDINGEN TAB - Live Feed
// ============================================================
// Great-circle distance in km, or null when either point is unknown. Lives at
// module level because the board and the intelligence screen both need it and
// a second copy would be a second thing to get wrong.
// A glance-sized map above the feed: where the reports around you actually
// are, without leaving the list. Deliberately inert — no dragging, no zooming.
// A half-interactive map sitting above a scrolling list fights the scroll, and
// the full map is one tap away on its own tab.
const FeedMap = ({ meldingen, hotspots = [], userLocation, onOpen, onReportClick,
                  gekozenTypes = [], onFilter }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Amsterdam is the same fallback the rest of the app uses when there is no
  // fix yet, so the map shows a country rather than an empty grey square.
  const center = userLocation || [52.3676, 4.9041];
  // Everything around you, deliberately unfiltered: the list is scoped to your
  // own road, and the map is where you look to see what else is going on.
  const pins = meldingen.filter(m => m.lat != null && m.lon != null).slice(0, 40);
  // De rails horen bij dezelfde veertig.
  //
  // Ze werden over de héle lijst berekend en niet over `pins`, en die lijst is
  // sinds de kaartspelden landelijk zijn 1857 lang. Gemeten op dit kaartje van
  // 366×178: veertig spelden en vijfenvijftig paaltjes van trajecten door het
  // hele land, waarvan de meeste honderden kilometers verderop liggen. Een
  // ring op de rand van het beeld die nergens bij hoort.
  // A section control is not a point. It measures your average over a stretch,
  // so the stretch is the thing worth drawing — a pin at one end says almost
  // nothing about where you are still being timed. Stored [lon, lat]; Leaflet
  // wants [lat, lon].
  const sections = pins
    .filter(m => Array.isArray(m.section) && m.section.length > 1)
    .map(m => {
      const line = m.section.map(([lon, lat]) => [lat, lon]);
      // De twee palen. Dat is wat er fysiek staat; de rest is de afstand
      // ertussen waarover je gemiddelde loopt.
      return { id: m.id, report: m, line, ends: [line[0], line[line.length - 1]] };
    });
  // A handful at most: these are soft background shapes, and a screen full of
  // amber circles says "everywhere" instead of "here".
  const zones = hotspots.filter(h => h.lat != null && h.lon != null).slice(0, 6);

  return (
    <div className="feed-map" data-testid="feed-map">
      {ready ? (
        <MapContainer
          center={center}
          zoom={userLocation ? 13 : 8}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          keyboard={false}
          attributionControl={false}
        >
          <TileLayer url={TEGELS} attribution={TEGELS_BRON} />
          <TileLayer url={TEGELS_NAMEN} />
          {/* Patronen first, so live reports sit on top of the history rather
              than under it. Where it has happened before is context; what is
              there now is the news. */}
          {zones.map((h) => (
            <Circle
              key={`hs-${h.id}`}
              center={[h.lat, h.lon]}
              radius={h.radius_meters || 500}
              pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b",
                             fillOpacity: 0.12, weight: 1, opacity: 0.35 }}
            />
          ))}
          {/* Altijd bovenop. Leaflet stapelt spelden op breedtegraad, dus wie
              er boven ligt hangt van het toeval af — en zolang de spelden
              vlakke stippen van elf pixel waren, viel dat niet op. Met schijven
              van zeventien plus een ring verdween "jij" onder de eerste de
              beste melding die toevallig noordelijker lag. Van de weg gemeld,
              en terecht: het chipje "Jij" stond er nog wel, de stip niet meer.
              2000 en niet 1000, want dat is wat een geselecteerde speld op de
              volle kaart al draagt. */}
          {userLocation && <Marker position={userLocation} icon={userLocationIcon} zIndexOffset={2000} />}
          {sections.map((s) => (
            <React.Fragment key={`sec-${s.id}`}>
              <Polyline
                positions={s.line}
                bubblingMouseEvents={false}
                eventHandlers={{ click: () => onReportClick?.(s.report) }}
                pathOptions={{
                  color: "#fca5a5", weight: 2.5, opacity: 0.5, lineCap: "round",
                }}
              />
              {s.ends.map((p, i) => (
                <Marker key={`end-${s.id}-${i}`} position={p} icon={sectionEndIcon}
                  title={`Details: ${s.report.authority_name || "Trajectcontrole"}`}
                  alt="Trajectcontrole"
                  bubblingMouseEvents={false}
                  eventHandlers={{ click: () => onReportClick?.(s.report) }}
                />
              ))}
            </React.Fragment>
          ))}
          {/* Kleiner dan op de volle kaart, en dat is gemeten en niet
              gekozen: dit vak is 178 px hoog en toont er veertig. Op de maat
              van de grote kaart bedekken die een derde van het vlak dat ze
              moeten beschrijven — precies de fout die de emoji-ballonnen
              maakten.

              Wat hier ooit stond en weer weg is: de spelden buiten "Op mijn
              route" dempen. Dat kan niet. /api/map levert `on_route` noch
              `ahead` — die twee komen van /api/board — dus was de regel altijd
              waar en deed hij niets. Stilletjes niets doen is erger dan het
              niet hebben. */}
          {pins.map((m) => (
            <Marker
              key={m.id}
              position={[m.lat, m.lon]}
              title={`Details: ${m.authority_name || m.control_name || "Melding"} — ${m.address || m.city || ""}`}
              alt={m.authority_name || m.control_name || "Melding"}
              bubblingMouseEvents={false}
              eventHandlers={{ click: () => onReportClick?.(m) }}
              icon={serviceDot(m, {
                compact: false,
                faded: m.lifecycle_status === "possibly_ended",
              })}
            />
          ))}
        </MapContainer>
      ) : (
        <MapSpinner />
      )}
      {/* The key explains only what a reader cannot work out: the amber blobs,
          and which dot is them. There is deliberately no "Live melding" entry
          any more — the report dots take the colour of their service, so one
          red swatch beside a green ILT dot would have been a legend that
          contradicts its own map. What those colours mean is answered by the
          service chips directly below, which carry the same colours.
          The mockup's "Forecast" entry stays out for the older reason: this
          map does not paint one. Its locate and navigate buttons too — the map
          is inert and always centred on you, so both would do nothing. */}
      {/* De typekeuze, in de hoek van het kaartje en niet op de knoppenregel
          eronder. Die regel draagt nu alleen nog de schakelaar, over de volle
          breedte — gevraagd, en terecht: twee vragen op één rij waarvan er één
          een menu opent, las als drie standen naast elkaar.

          Hier kost hij geen enkele verticale pixel, en hij staat op het ding
          dat hij filtert. Het getal verschijnt alleen als er iets aanstaat: een
          filter die je niet ziet staan is precies wat dit scherm eerder
          verwarrend maakte. */}
      {onFilter && (
        <button
          className={`feed-map-filter ${gekozenTypes.length ? "on" : ""}`}
          onClick={(e) => { e.stopPropagation(); onFilter(); }}
          aria-label={gekozenTypes.length
            ? `Filter: ${gekozenTypes.length} soorten gekozen`
            : "Filteren op soort"}
          data-testid="kaart-filter"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M2.5 3.5h11L9.2 8.6v4.2l-2.4 1.2V8.6z" />
          </svg>
          {gekozenTypes.length > 0 && <b>{gekozenTypes.length}</b>}
        </button>
      )}
      <div className="feed-map-legend">
        {zones.length > 0 && <span><i className="lg-pattern" />Patroon</span>}
        <span>◎ &nbsp;Om je heen</span>
      </div>
      <button type="button" className="feed-map-open" onClick={onOpen} aria-label="Kaart openen"
        style={{ fontFamily: "inherit", cursor: "pointer" }}>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M21 3 3 10l7 3 3 8z" /></svg>
      </button>
    </div>
  );
};

function haversineKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Everything that is active, for whatever draws a map.
 *
 * The board's own answer is cut down hard on purpose — a radius per kind, your
 * own carriageway, your own direction, a cap — and every one of those cuts is
 * right for a list you scroll at 100 km/h and wrong for a map. /api/map filters
 * on one thing: is this still active.
 *
 * It lives here rather than inside the map tab because two screens draw the
 * same picture: the full map, and the glance-sized one above the board. Two
 * copies of this would be two requests and, worse, two moments in time — the
 * small map and the big one could disagree about what is out there.
 *
 * Null until the first answer lands, so callers can fall back to whatever they
 * already have and never blank a map they had just drawn.
 */
const useMapPins = (userLocation) => {
  const [pins, setPins] = useState(null);
  // Wat er het laatst gehaald is, en of er nog iets loopt. Allebei in een ref:
  // ze sturen wanneer er gehaald wordt en horen geen hertekening uit te lokken.
  const gehaald = useRef(null);
  const loopt = useRef(false);

  useEffect(() => {
    let cancelled = false;

    /* Eén kaartverzoek tegelijk, en een grof gebiedsanker.
       ------------------------------------------------------------------
       Dit hing aan `userLocation`, en dat is sinds watchPosition het anker van
       honderdvijftig meter. Bij 100 km/u schuift dat elke 5,4 seconden op,
       terwijl /api/map p50 9,78 seconden kost — gemeten op 11 september tegen
       productie, zie docs/performance-collaboration.md. De verzoeken
       overlapten dus permanent.

       En dat raakt niet alleen de kaart. In datzelfde onderzoek deed
       /api/stats 9,36 seconden terwijl er één kaartverzoek liep, tegen
       0,32-0,63 normaal: één kaart houdt de hele server bezig. Eén rijdende
       gebruiker blokkeerde daarmee zijn eigen bord.

       Twee kilometer dus, op een kaart die er vijftien toont — en niet
       helemaal los van je locatie, want dan kijk je na een uur rijden nog naar
       je vertrekpunt. Zie ophaalTempo.js. */
    const load = async (nu) => {
      if (loopt.current) return;
      if (!moetOphalen(gehaald.current, nu, TEMPO.kaart)) return;
      loopt.current = true;
      try {
        // Met een straal, en dat is het halve verhaal van de snelheid: de
        // server staat standaard op 75 km, waar de kwadratische clustering
        // 5000 documenten van krijgt. Op 25 km zijn het er 379 en duurt het
        // 1,24 s in plaats van 12,04. Gemeten, zie ophaalTempo.js.
        const q = nu.lat != null
          ? `?lat=${nu.lat}&lon=${nu.lon}&radius_m=${KAART_STRAAL_M}`
          : "";
        const res = await axios.get(`${API}/map${q}`);
        if (cancelled) return;
        gehaald.current = nu;
        setPins(res.data.pins || []);
      } catch (e) {
        // Keep whatever is on screen. A failed poll has never been a reason to
        // blank a map.
        console.error("Kaart laden mislukt:", e);
      } finally {
        loopt.current = false;
      }
    };

    const nu = () => ({
      lat: userLocation ? userLocation[0] : null,
      lon: userLocation ? userLocation[1] : null,
      atMs: Date.now(),
    });
    load(nu());
    // Elke tien seconden kijken óf het mag; de drempels in ophaalTempo
    // bepalen of er werkelijk iets uitgaat.
    const t = setInterval(() => load(nu()), 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [userLocation]);
  return pins;
};

/**
 * De trajectband: je gemiddelde terwijl je er middenin zit.
 *
 * ------------------------------------------------------------------
 * Gemeld van de weg: "ik ga de snelweg op, bevind me midden in een traject,
 * start de trajectmodule op zoals Flitsmeister." De rekenkern daarvoor bestond
 * al twee keer — SectionRun.kt en SectionRun.swift — maar de mini app had er
 * niets van: die tekende de lijn en verder niets, en had bovendien maar één
 * positiemeting per sessie. Zie sectionRun.js en driveFix.js.
 *
 * Deze component hangt aan de positiestroom en niet aan de state van de app,
 * zodat één keer per seconde alléén deze balk opnieuw tekent en niet het hele
 * scherm.
 *
 * Hij toont zichzelf niet als er niets te zeggen valt. Onder de limiet ben je
 * al aan het doen wat je moet doen, en dan is het getal informatie; erboven is
 * het een advies. Vandaar dat "hoe hard mag de rest nog" pas verschijnt als je
 * erover zit — zie allowedRestKmh: onder de limiet komt daar een getal uit dat
 * bóven de limiet ligt, en dat mag een app nooit op een scherm zetten.
 */
const TrajectBand = ({ meldingen, tolerantieKmh = 5, onActiveChange,
                      voertuig, voertuigExtra }) => {
  const { profielen } = useConfig();
  const [run, setRun] = useState(null);
  // De rijen zijn er via een ref bij, zodat de luisteraar niet elke poll
  // opnieuw wordt aangemeld — en zodat hij nooit met een oude lijst rekent.
  const rijen = useRef(meldingen);
  rijen.current = meldingen;

  useEffect(() => opFix((fix) => {
    setRun((vorige) => volgTraject(vorige, rijen.current, {
      lat: fix.lat, lon: fix.lon, heading: fix.heading, nowMs: fix.atMs,
    }));
  }), []);

  const active = run != null;
  useEffect(() => { onActiveChange?.(active); }, [active, onActiveChange]);
  if (!run) return null;

  const gem = averageKmh(run);
  const rest = remainingM(run);

  // Wat er voor dít voertuig geldt. Een vrachtwagen rijdt 80 waar 100 staat,
  // en de trajectcontrole rekent hem daarop af — dus moet de waarschuwing en
  // het advies met dat getal rekenen, niet met het bord.
  //
  // Het geposte getal blijft staan en wordt níet vervangen. Dat is de kortste
  // oplossing en de duurste fout: dan kun je de app niet meer naast het bord
  // leggen, en 80 op een 100-galg leest als een app die achterloopt op een
  // wegwerkzaamheid.
  const eigenLimiet = effectieveLimiet(run.limitKmh, profielen, voertuig, voertuigExtra);
  const eigenWijktAf = limietWijktAf(run.limitKmh, profielen, voertuig, voertuigExtra);
  const mijnRun = eigenWijktAf ? { ...run, limitKmh: eigenLimiet } : run;

  const erover = isOver(mijnRun, tolerantieKmh);
  const advies = erover ? allowedRestKmh(mijnRun) : null;
  const afstand = rest >= 1000
    ? `${(Math.round(rest / 100) / 10).toLocaleString('nl-NL')} km`
    : `${Math.round(rest / 100) * 100} m`;

  return (
    <div className={`traject-band ${erover ? 'over' : ''}`} data-testid="traject-band">
      <div className="tb-kop">
        <span className="tb-merk">Traject</span>
        {run.limitKmh != null && <span className="tb-limiet">max {run.limitKmh}</span>}
        {eigenWijktAf && (
          <span className="tb-eigen" data-testid="traject-eigen-limiet">
            voor jou {eigenLimiet}
          </span>
        )}
        <span className="tb-rest">nog {afstand}</span>
      </div>
      <div className="tb-lijf">
        <div className="tb-gem">
          {gem != null ? (
            <><b>{gem}</b><span className="tb-eenheid">km/u gemiddeld</span></>
          ) : (
            // De eerste driehonderd meter is elk getal ruis: een seconde
            // GPS-jitter over vijftig meter is elke tik een ander cijfer.
            <span className="tb-wacht">Meten…</span>
          )}
        </div>
        {advies != null && (
          <div className="tb-advies">
            <span className="tb-advies-op">{advies}</span>
            <span className="tb-advies-bij">de rest rijden</span>
          </div>
        )}
      </div>
      {/* Hoe ver je bent. Eén balkje, want dit is het enige dat een
          trajectcontrole anders maakt dan een paal: er is altijd nog wat te
          doen aan het gemiddelde. */}
      <div className="tb-spoor" aria-hidden="true">
        <div className="tb-voortgang" style={{
          width: `${Math.min(100, Math.max(0, (run.lastOffsetM / Math.max(1, run.lengthM)) * 100))}%`,
        }} />
      </div>
    </div>
  );
};

// Eén typemenu en één selectie voor bord en kaart.
const TypeFilterMenu = ({ options, selected, total, onChange }) => (
  <div className="svc-menu-group">
    <div className="svc-menu-label">Type</div>
    <button role="menuitemradio" aria-checked={!selected.length}
      className={`svc-menu-row ${!selected.length ? "on" : ""}`}
      onClick={() => onChange([])}>
      <span className="svc-menu-name">Alle types</span>
      <span className="svc-menu-count">{total}</span>
    </button>
    {options.map(({ id, name, count }) => (
      <button key={id} role="menuitemcheckbox" aria-checked={selected.includes(id)}
        className={`svc-menu-row ${selected.includes(id) ? "on" : ""}`}
        onClick={() => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id])}>
        <span className={`svc-menu-tick ${selected.includes(id) ? "on" : ""}`} aria-hidden="true" />
        <span className="svc-menu-name">{name}</span>
        <span className="svc-menu-count">{count}</span>
      </button>
    ))}
  </div>
);

const MeldingenTab = ({ meldingen, mapPins, nietVoorMij = 0, voertuig, voertuigExtra, hotspots = [], loading, onReportClick, userLocation, currentRoad, currentHectometer, hunch, onOpenMap, onOpenSettings, filters, setFilters }) => {
  const [showFilter, setShowFilter] = useState(false);
  /* Where and what, plus the city and the ordering from the menu.
     `where` is a two-position switch and `type` is one choice or none — see
     boardFilter.js for why those two shapes, and for the rule they keep.

     Nearest first, not newest first. This screen answers "what is coming up",
     so the top row has to be the thing you reach first — a report 10 km back
     sitting above one 2 km ahead reads as an instruction to worry about the
     wrong thing. The other two orderings are in the menu. */

  const [sectionActive, setSectionActive] = useState(false);
  const listMeldingen = useMemo(() => sectionActive
    ? meldingen.filter(m => m.authority !== 'trajectcontrole') : meldingen,
    [meldingen, sectionActive]);

  // Calculate distance between two points in km using Haversine formula
  const calculateDistance = haversineKm;
  
  // Format distance for display
  /* Dutch separator, not the JavaScript default. The native app has always
     printed "13,3 km" and this printed "13.3 km" for the same report — a small
     thing, but the two apps show the same board and shouldn't spell a number
     two ways. */
  const formatDistance = useCallback((distanceKm) => {
    if (distanceKm === null) return null;
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1000)} m`;
    }
    return `${distanceKm.toLocaleString("nl-NL", {
      minimumFractionDigits: 1, maximumFractionDigits: 1,
    })} km`;
  }, []);
  
  // Check if proximity sorting is available
  const proximityAvailable = userLocation && userLocation[0] && userLocation[1];
  
  const toggleCityFilter = (city) => {
    setFilters(prev => ({
      ...prev,
      cities: prev.cities.includes(city)
        ? prev.cities.filter(c => c !== city)
        : [...prev.cities, city]
    }));
  };

  /* Back to the board as it opens: your own road, no city, nearest first. */
  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTER, cities: [], sort: DEFAULT_SORT });
  };

  // Extract unique cities from meldingen
  const availableCities = useMemo(() => {
    const cities = [...new Set(meldingen.map(m => m.city).filter(Boolean))];
    return cities.sort();
  }, [meldingen]);
  
  // Memoized filtered meldingen with distance calculation
  const filteredMeldingen = useMemo(() => {
    // How far away each one is.
    //
    // From the server when it says so: it measures over the road where it can
    // — the difference of two offsets along the road you are both on, or the
    // nearest point of a section control's whole stretch. A straight line here
    // is what this used to draw for everything, which meant a camera you reach
    // by driving round a body of water read 616 m on this screen and 2,2 km on
    // the native one. Same report, same moment, two numbers.
    //
    // Its own line stays as the fallback for a response that carries none.
    const withDistance = listMeldingen.map(m => ({
      ...m,
      distance: m.distance_m != null
        ? m.distance_m / 1000
        : (userLocation ? calculateDistance(userLocation[0], userLocation[1], m.lat, m.lon) : null),
    }));
    
    // See boardFilter.js — every control on the row promises a number, and
    // this is the function that keeps the promise. The map beside this list
    // still draws every report there is, which is what a map is for.
    const filtered = visibleReports(withDistance, { ...filters, where: WHERE_ROAD }, filters.cities);
    
    // Sort based on selected option
    const byNewest = (a, b) => new Date(b.timestamp) - new Date(a.timestamp);
    return filtered.sort((a, b) => {
      if (filters.sort === "newest") {
        return byNewest(a, b);
      }
      if (filters.sort === "confirmations") {
        return b.confirmations - a.confirmations;
      }
      if (filters.sort === "proximity") {
        // No location at all: every distance is null, and falling back to
        // newest keeps the default ordering meaningful instead of leaving the
        // list in whatever order it arrived in.
        if (a.distance === null && b.distance === null) return byNewest(a, b);
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      }
      return 0;
    });
  }, [listMeldingen, filters, userLocation, calculateDistance]);


  /* Confirming, releasing and sharing all used to live here, behind three
     buttons under every row. They now sit on the detail screen — see
     IntelligenceDetail — which is where the mockup puts them and where there
     is room to say what each one does. */

  const activeFiltersCount = filters.cities.length + (filters.sort !== DEFAULT_SORT ? 1 : 0);

  /* What every chip on the row promises.
     ------------------------------------------------------------------
     Counted with the very function the list filters by, so a chip cannot
     promise a taxi check above a list that says there is nothing on this road.
     Both used to be right at once, which is the sort of correctness nobody can
     use.

     Counted over everything loaded rather than over the current view, because
     these are alternatives to the current view: "Mobiele flitser 4" has to mean
     "tap me and see four", whichever scope you are in now. */
  /* Both sides of the switch, counted within whatever type is chosen.

     "Op mijn weg 1 · Alles 3" while filtering on fixed cameras is two true
     statements about the same three cameras, and it is what makes sliding the
     switch a decision rather than a guess. */
  const roadCount = useMemo(
    () => countFor(listMeldingen, { ...filters, where: WHERE_ROAD }, filters.cities),
    [listMeldingen, filters],
  );


  /* The menu's types, counted at "Alles" because that is where tapping one
     lands you. All of them, not the three that fit on a row — a menu can hold
     thirteen without spilling, which is the whole reason they moved here. */
  // De rang erbij, zodat waar je naar zoekt bovenaan staat en niet onder de
  // aantallen van NDW. Zie TONE_RANK in boardFilter.js; de toon komt uit
  // authorityMark, want die tabel is de enige plek waar de woordenschat staat.
  const typeRang = useCallback(
    (id) => TONE_RANK[authorityMark(id, id).tone],
    [],
  );
  const typeOptions = useMemo(
    () => typeChoices(mapPins ?? meldingen, [], typeRang),
    [mapPins, meldingen, typeRang],
  );
  const allTypesCount = useMemo(
    () => countFor(mapPins ?? meldingen, { where: WHERE_ALL, types: [] }, []),
    [mapPins, meldingen],
  );

  // Only worth offering when there is more than one to choose between —
  // filtering a board that is all one city by that city does nothing.
  const cityChoices = availableCities.length > 1 ? availableCities : [];

  const boardCount = filteredMeldingen.length;

  // In "Alles" per type bij elkaar — zie groupByType. Als één platte lijst met
  // koppen ertussen, zodat het paneel één paneel blijft en de rijen precies de
  // rijen blijven die er zonder groepering ook stonden.


  return (
    <div className="tab-content meldingen-tab">
      {/* One header instead of two. There used to be a row of national totals
          (actief / totaal / melders) above a separate road banner, and they
          contradicted each other: the totals were counted from this road while
          reading as country-wide. What a driver needs is which road they are
          being told about and how much is on it — so that is all this says. */}
      <div className="feed-head" data-testid="feed-head">
        <div className="feed-head-text">
          {/* The road name is where *you* are, not what the list is filtered
              to — only official items get held to your street. Without this
              line the title read as a scope the list never had. */}
          {currentRoad && <div className="feed-head-over">Je locatie</div>}
          <h2 className="feed-head-title">
            <span className="feed-head-dot" aria-hidden="true" />
            {currentRoad || 'Live overzicht'}
          </h2>
          {currentHectometer && <div className="feed-head-sub">{currentHectometer}</div>}
          {/* "op deze weg" was not true. Only official items are filtered to
              the caller's street (on_same_road); community reports are not, so
              this counted three reports on three other streets kilometres away
              and called them one road. Now that the street heads every row, the
              contradiction is on screen. The title still names the road you are
              on — that part was never wrong. */}
          {/* En de onderregel volgt de schakelaar. "8 meldingen in de buurt"
              boven een lijst die op je route staat en er twee toont, zijn twee
              verhalen over hetzelfde scherm. */}

        </div>
        {/* Where the mockup has its two round buttons. Only one of them does
            anything here, so only one is drawn. */}
        <button className="feed-head-btn" onClick={onOpenSettings}
                data-testid="settings-btn" aria-label="Instellingen">⚙</button>
      </div>

      {/* Boven de kaart en boven de lijst, want zolang hij er staat is dit het
          enige op dit scherm waar je iets mee moet. Hij tekent zichzelf niet
          als je niet in een traject zit. */}
      <TrajectBand meldingen={meldingen} onActiveChange={setSectionActive}
                   voertuig={voertuig} voertuigExtra={voertuigExtra} />

      {/* De omgeving, niet de lijst.
          ------------------------------------------------------------------
          This drew `filteredMeldingen` while its own comment said the opposite
          — "everything around you, deliberately unfiltered" — so with the
          switch on "Op mijn weg" and a short list it was 158 px of dark with
          your own blue dot in it. Exactly when you most want to know what is
          around you.

          The list and the map answer different questions, which is the whole
          reason both are on this screen. `filteredMeldingen` remains the
          fallback until the first map answer lands, so it can never be emptier
          than it is today. */}
      <FeedMap
        meldingen={pinsOfTypes(mapPins ?? meldingen, filters.types)}
        hotspots={hotspots}
        userLocation={userLocation}
        onOpen={onOpenMap}
        onReportClick={onReportClick}
        gekozenTypes={filters.types}
        onFilter={() => setShowFilter(v => !v)}
      />

      {/* Two questions, two controls, and the row cannot outgrow the screen.
          ------------------------------------------------------------------
          **Waar** is a switch with exactly two positions, so its width is
          fixed. **Wat** is a menu, so it can hold thirteen types without
          spilling. Between them sits at most one chip: the type you picked,
          with an X on it.

          The version before this put every service on the row as its own chip
          and it ran off the side of the screen — "Vaste flitse…" cut off at the
          edge, which is a control you cannot see. Before that there were three
          things narrowing one list at once with four separate counts, none of
          which was the length of it.

          Every number here is the number of rows tapping it gives you. That is
          the one property worth defending; see boardFilter.js. */}
      <div className="svc-bar">
        <div className="board-route-heading" data-testid="board-route-heading">
          <h3>Vóór je</h3>
          <span className="board-route-count" aria-label={`${roadCount} meldingen op je route`}>{roadCount}</span>
        </div>

        {/* Wat je aan hebt staan, en de weg eruit — per soort één chip.

            Niet verstopt in het menu: een filter waarvan je niet ziet dát je
            hem gezet hebt, is wat dit scherm eerder verwarrend maakte. En niet
            naast de schakelaar geperst: op 390 px knipte dat de tellingen af,
            en dat zijn juist de twee getallen waarvoor de schakelaar bestaat.

            Bij drie of meer één regel in plaats van drie chips, want anders
            duwt het filter zelf de lijst van het scherm die het moest
            opschonen. */}
        {filters.types.length > 0 && (
          <div className="type-row">
            {filters.types.length < 3 ? filters.types.map(id => {
              const naam = typeOptions.find(o => o.id === id)?.name || id;
              return (
                <button
                  key={id}
                  className={`type-chip m-${authorityMark(id, naam).tone}`}
                  onClick={() => setFilters(f => toggleType(f, id))}
                  data-testid="chip-type"
                  aria-label={`${naam} — filter wissen`}
                >
                  <span className="type-name">{naam}</span>
                  <span className="type-x" aria-hidden="true">×</span>
                </button>
              );
            }) : (
              <button
                className="type-chip"
                onClick={() => setFilters(f => allTypes(f))}
                data-testid="chip-type"
                aria-label="Alle soorten weer tonen"
              >
                <span className="type-name">{filters.types.length} soorten gekozen</span>
                <span className="type-x" aria-hidden="true">×</span>
              </button>
            )}
          </div>
        )}

        {/* The types, the city and the ordering — everything that is a list
            rather than a switch.

            It was a full-screen sheet once, listing every type the app knows,
            thirteen of them with emoji, on a board that usually holds one kind:
            a catalogue, not a filter. Then the types moved onto the row and the
            row ran off the screen. A menu is the right home for something that
            grows; a switch is the right home for something that never does.

            Only what is actually out there, with the count you get by tapping
            it. Nothing at zero, because a filter that can only empty the screen
            is not a choice. */}
        {showFilter && (
          <>
            <div className="svc-menu-scrim" onClick={() => setShowFilter(false)} />
            <div className="svc-menu" role="menu" data-testid="filter-menu">
              <TypeFilterMenu options={typeOptions} selected={filters.types}
                total={allTypesCount}
                onChange={types => setFilters(f => ({ ...f, types }))} />

              {cityChoices.length > 0 && (
                <div className="svc-menu-group">
                  <div className="svc-menu-label">Plaats</div>
                  {cityChoices.map(city => {
                    const on = filters.cities.includes(city);
                    return (
                      <button
                        key={city}
                        role="menuitemcheckbox"
                        aria-checked={on}
                        className={`svc-menu-row ${on ? "on" : ""}`}
                        onClick={() => toggleCityFilter(city)}
                        data-testid={`city-filter-${city}`}
                      >
                        <span className="svc-menu-name">{city}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="svc-menu-group">
                <div className="svc-menu-label">Sorteren</div>
                {SORT_OPTIONS.map(({ id, label, needsLocation }) => {
                  const usable = !needsLocation || proximityAvailable;
                  return (
                    <button
                      key={id}
                      role="menuitemradio"
                      aria-checked={filters.sort === id}
                      className={`svc-menu-row ${filters.sort === id ? "on" : ""}`}
                      disabled={!usable}
                      title={usable ? undefined : "Locatie niet beschikbaar"}
                      onClick={() => {
                        setFilters(prev => ({ ...prev, sort: id }));
                        setShowFilter(false);
                      }}
                    >
                      <span className="svc-menu-name">{label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Only when there is something to undo. A permanent reset button
                  is a permanent suggestion that something is wrong. */}
              {(activeFiltersCount > 0 || !isDefault(filters)) && (
                <button
                  className="svc-menu-reset"
                  onClick={() => { resetFilters(); setShowFilter(false); }}
                >
                  Terug naar mijn weg
                </button>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* The sort bar that used to sit here is gone. It offered the same three
          options the menu behind "Meer" already carries, and a permanent row of
          three buttons for a choice most people make once is space the board
          can spend on what is actually on the road. */}

      {/* The share sheet that stood here is gone with the button that opened
          it. Sharing is a tap on ↗ on the detail screen, which hands straight
          to Telegram's own share sheet instead of asking you to pick from ours
          first. */}

      {/* An empty board is the normal state, not a failure: it means the road
          ahead is clear. It sits outside the list panel, because a panel drawn
          around a checkmark reads as an empty container rather than good
          news. */}
      {!loading && filteredMeldingen.length === 0 && (
        <div className="board-clear" data-testid="board-empty">
          <span className="clear-ring">✓</span>
          <div>
            {/* Met een profiel kan "leeg" drie dingen betekenen: er staat
                niets, je filters sluiten het uit, of het gaat jóu niet aan.
                Die derde hoort erbij te staan — anders leest dit scherm als
                "de weg is vrij" terwijl er een weegcontrole staat. Zelfde
                regel als de dekkingsregel op het ritscherm. */}
            <h4>{nietVoorMij > 0 ? "Niets dat jou aangaat" : "Geen meldingen bekend"}</h4>
            <p>{filters.types.length || filters.cities.length ? "Binnen je gekozen filters" : "In jouw richting"}
            </p>
            {nietVoorMij > 0 && (
              <p className="niet-voor-mij" data-testid="board-niet-voor-mij">
                Wel {nietVoorMij} {nietVoorMij === 1 ? "melding" : "meldingen"} voor
                een ander voertuig.{" "}
                <button type="button" className="link-knop" onClick={onOpenSettings}>
                  Profiel wijzigen
                </button>
              </p>
            )}
          </div>
        </div>
      )}

      {/* The note that used to sit here — "3 meldingen niet op je route ·
          toon" — is gone. It was a fourth control saying what the "Alles" chip
          already says, with a number nobody could line up against the two rows
          below it. A chip that reads "Alles 5" next to "Op mijn weg 2" is the
          same offer, made once, in a place you were already looking. */}

      {/* One panel, hairlines between the rows. */}
      <div className="meldingen-feed">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          filteredMeldingen.map(melding => {
            const { shield, road, rest } = roadLine(melding);
            const kleur = serviceColour(melding.authority, melding.authority_name);
            const meta = [
              formatDistance(melding.distance),
              melding.hectometer?.replace(/^.*? · /, ""),
              melding.timestamp ? timeAgo(melding.timestamp) : null,
            ].filter(Boolean).join(" · ");
            const twijfel = melding.lifecycle_status === 'possibly_ended' || melding.lifecycle_status === 'possibly_removed';
            return (
              <button type="button" key={melding.id}
                className={`board-report-card ${twijfel ? 'possibly-gone' : ''}`}
                data-testid={`melding-${melding.id}`}
                onClick={() => onReportClick?.(melding)}>
                <span className="board-report-symbol" style={{ background: kleur }} aria-hidden="true">
                  <svg viewBox="0 0 16 16" dangerouslySetInnerHTML={{
                    __html: glyphShape(glyphFor(melding.authority,
                      authorityMark(melding.authority, melding.authority_name).tone)),
                  }} />
                </span>
                <span className="board-report-copy">
                  <strong>{cardTitle(melding)}</strong>
                  <span className="board-report-place">{[shield?.label, road || rest].filter(Boolean).join(' · ') || 'Locatie op kaart'}</span>
                  {meta && <span className="board-report-meta">{meta}</span>}
                  {twijfel && <span className="board-report-warning">Mogelijk beëindigd of vrijgegeven</span>}
                  {melding.report_state === 'pending' && <span className="board-report-warning">Wacht op bevestiging · nog geen geluid</span>}
                  {melding.flow_hint && <span className="board-report-warning">{melding.flow_hint}</span>}
                </span>
                <span className="board-report-chevron" aria-hidden="true">›</span>
              </button>
            );
          })
        )}
      </div>

      {/* Context, not a report — so it sits under the things that are actually
          there, outside their panel, in a quieter colour, with no distance and
          nothing to tap. Shown on an empty board too: "nothing right now, but
          this spot has a history at this hour" is worth knowing. */}
      {!loading && hunch && (
        <div className="hunch" data-testid="board-hunch">
          <span className="hunch-icon" aria-hidden="true">◈</span>
          <span>{hunch}</span>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MELDEN TAB - Quick Report (3 taps)
// ============================================================
const MeldenTab = ({ onSubmit, userId, username, voertuig }) => {
  // Reporting needs the live GPS stream, not the board's 150 m / 30 s anchor.
  const [reportFix, setReportFix] = useState(huidigeFix);
  useEffect(() => opFix(setReportFix), []);
  const freshFix = reportFix && Date.now() - reportFix.atMs <= 15000 ? reportFix : null;
  const userLocation = freshFix ? [freshFix.lat, freshFix.lon] : null;
  const userHeading = freshFix?.heading ?? null;
  const userAccuracy = freshFix?.accuracyM ?? null;
  const manualPin = useRef(false);
  const { types, profielen } = useConfig();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pinLocation, setPinLocation] = useState(null);
  // Whether the user actually moved the pin, as opposed to it being seeded
  // from their own position when the step opened. The two were indistinguish-
  // able, and the report body asked "is there a pin?" to decide whether the
  // reporter's own heading and accuracy still described the spot — so the
  // answer was always "yes" and both were always dropped.
  const [pinMoved, setPinMoved] = useState(false);
  const [pinAddress, setPinAddress] = useState(null);
  // Voor welke rijrichting de controle geldt. "mine" is wat er tot nu toe
  // gebeurde — de koers van de melder werd zonder meer opgeslagen als de
  // richting waar de melding voor gold.
  const [direction, setDirection] = useState("mine");
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [addressQuery, setAddressQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (step !== 3 || manualPin.current || !reportFix
        || Date.now() - reportFix.atMs > 15000) return;
    setPinLocation([reportFix.lat, reportFix.lon]);
    setPinAddress(null);
  }, [reportFix, step]);

  // Everything the Melden screen can pick, for the summary line on step 2.
  const allTypes = types;

  // Set pin to user location when entering step 3
  const goToStep3 = () => {
    manualPin.current = false;
    setPinLocation(userLocation ? [...userLocation] : null);
    setPinMoved(false);
    setPinAddress(null);
    setStep(3);
  };
  
  /*
   * One tap, then the location. There used to be a second question — "what
   * kind of check?" — for the half of the choices that were services rather
   * than types. The type is the whole answer now, and the server fills in the
   * old second field from it, so nothing downstream notices.
   */
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedSubtype(TYPE_BY_ID(types, type)?.control_type || 'algemeen');
    goToStep3();
  };

  // Reverse geocode pin location
  const reverseGeocode = async (lat, lon) => {
    try {
      const resp = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`);
      const addr = resp.data?.address;
      if (addr) {
        const road = addr.road || addr.pedestrian || addr.suburb || '';
        const house = addr.house_number || '';
        const city = addr.city || addr.town || addr.village || '';
        setPinAddress(`${road} ${house}${city ? `, ${city}` : ''}`.trim());
      }
    } catch (e) {
      console.error('Geocode failed:', e);
    }
  };

  const handlePinDragEnd = (e) => {
    manualPin.current = true;
    const { lat, lng } = e.target.getLatLng();
    setPinLocation([lat, lng]);
    setPinMoved(true);
    reverseGeocode(lat, lng);
  };

  const handleRecenter = () => {
    if (userLocation) {
      manualPin.current = false;
      setPinLocation([...userLocation]);
      setPinMoved(false);
      setPinAddress(null);
      reverseGeocode(userLocation[0], userLocation[1]);
      setRecenterTrigger(prev => prev + 1);
    }
  };

  const handleAddressSearch = async (e) => {
    e.preventDefault();
    if (!addressQuery.trim() || searching) return;
    setSearching(true);
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&countrycodes=nl&limit=1&addressdetails=1`
      );
      if (resp.data?.length > 0) {
        const result = resp.data[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        manualPin.current = true;
        setPinLocation([lat, lon]);
        setPinMoved(true);
        const addr = result.address;
        const road = addr?.road || addr?.pedestrian || addr?.suburb || '';
        const house = addr?.house_number || '';
        const city = addr?.city || addr?.town || addr?.village || '';
        setPinAddress(`${road} ${house}${city ? `, ${city}` : ''}`.trim() || result.display_name?.split(',').slice(0, 3).join(','));
        setRecenterTrigger(prev => prev + 1);
        setAddressQuery('');
      } else {
        setPinAddress('Adres niet gevonden');
      }
    } catch (err) {
      console.error('Address search failed:', err);
    }
    setSearching(false);
  };
  
  const handleSubmit = async () => {
    if (isSubmitting) return;
    // Never silently submit the map fallback or a GPS position frozen on entry.
    const current = huidigeFix();
    const fresh = current && Date.now() - current.atMs <= 15000 ? current : null;
    const submitLocation = pinMoved ? pinLocation : (fresh ? [fresh.lat, fresh.lon] : null);
    if (!submitLocation) {
      alert('Wacht op je actuele locatie of plaats de pin handmatig op de kaart.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/report`, {
        authority: selectedType,
        control_type: selectedSubtype || 'algemeen',
        lat: submitLocation[0],
        lon: submitLocation[1],
        user_id: userId,
        username: username,
        // Send address from frontend geocoding (avoids backend rate limiting)
        address: pinAddress || null,
        // Only when reporting where we actually are. Once the pin has been
        // dragged the user is marking somewhere else, so their own course and
        // the accuracy of their own fix say nothing about that spot.
        heading: pinMoved ? null : fresh?.heading ?? null,
        // Alleen meesturen als er een koers is: zonder koers is "mijn kant"
        // een lege uitspraak, en dan staat de keuze ook niet op het scherm.
        direction: (!pinMoved && fresh?.heading != null) ? direction : null,
        accuracy_m: pinMoved ? null : fresh?.accuracyM ?? null
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setStep(1);
        setSelectedType(null);
        setSelectedSubtype(null);
        setPinLocation(null);
        setPinMoved(false);
        setPinAddress(null);
        setDirection("mine");
        if (onSubmit) onSubmit();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit:', error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail
        : error.response ? 'Melding mislukt, probeer opnieuw'
        : 'Geen verbinding. Controleer je internet en probeer opnieuw.');
    }
    setIsSubmitting(false);
  };
  
  const resetForm = () => {
    manualPin.current = false;
    setStep(1);
    setSelectedType(null);
    setSelectedSubtype(null);
    setPinLocation(null);
    setPinMoved(false);
    setPinAddress(null);
    setRecenterTrigger(0);
  };

  if (showSuccess) {
    return (
      <div className="tab-content melden-tab">
        <div className="success-screen">
          <span className="success-icon">✅</span>
          <span className="success-text">Melding verzonden!</span>
          <span className="success-hint">Bedankt voor je bijdrage</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content melden-tab">
      <div className="melden-header">
        <h2>🚨 Nieuwe Melding</h2>
        <p className="melden-hint">Selecteer in 3 stappen</p>
        {step > 1 && (
          <button className="back-btn" onClick={resetForm}>← Opnieuw</button>
        )}
      </div>

      {/* Step Indicator */}
      {/* Two dots, not three. The middle step — "what kind of check?" — is
          gone with the second field it filled in. */}
      <div className="step-indicator">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step === 1 ? 'current' : ''}`}>1</div>
        <div className={`step-line ${step >= 3 ? 'active' : ''}`}></div>
        <div className={`step ${step >= 3 ? 'active' : ''} ${step === 3 ? 'current' : ''}`}>2</div>
      </div>

      {/* Step 1: Select Type */}
      {step === 1 && (
        <div className="melden-step">
          <h3>Wat zie je?</h3>
          {/* Three blocks, straight from the catalogue rather than from three
              hand-kept lists here — adding a type used to mean editing the bot,
              this file and the native app, and they drifted. The bar down the
              left is the service; the reporter never picks that, it follows
              from the type. */}
          {TYPE_GROUPS.map(({ key, title }) => {
            // Alleen wat je voertuig aangaat, ook hier. Een automobilist krijgt
            // geen knop voor een weegcontrole aangeboden — de eigenaar vroeg
            // "zien en doorgeven", en half filteren is verwarrender dan niet
            // filteren.
            //
            // De prijs staat erbij omdat hij echt is: zo kan alleen een
            // vrachtwagenchauffeur een weegcontrole melden. Met weinig
            // vrachtrijders betekent dat weinig weegcontroles. Eén regel om te
            // draaien als dat knelt.
            const inGroup = types.filter(
              t => t.group === key && soortGaatMijAan(t, profielen, voertuig));
            if (inGroup.length === 0) return null;
            return (
              <div className="type-section" key={key}>
                <h4>{title}</h4>
                <div className="type-grid">
                  {inGroup.map(t => (
                    <button
                      key={t.id}
                      className="type-btn"
                      style={{ "--svc": SERVICE_COLOUR[t.tone] || SERVICE_COLOUR.pol }}
                      onClick={() => handleTypeSelect(t.id)}
                    >
                      <span className="type-name">{t.name}</span>
                      {t.hint && <span className="type-hint">{t.hint}</span>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 3: Pin Location on Map */}
      {step === 3 && (
        <div className="melden-step location-step">
          <h3>Plaats de pin op de controle</h3>
          <p className="pin-hint">Sleep de pin of zoek een adres</p>
          
          <form className="address-search" onSubmit={handleAddressSearch} data-testid="address-search-form">
            <input
              type="text"
              className="address-input"
              placeholder="Zoek adres, straat of plaats..."
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              data-testid="address-search-input"
            />
            <button
              type="submit"
              className="address-search-btn"
              disabled={searching || !addressQuery.trim()}
              data-testid="address-search-btn"
            >
              {searching ? '...' : '🔍'}
            </button>
          </form>
          
          <div className="location-preview">
            <div className="location-map-pin" style={{ position: "relative" }}>
              {(pinLocation || userLocation) ? (
                <>
                <MapContainer
                  center={pinLocation || userLocation}
                  zoom={16}
                  style={{ height: "260px", width: "100%", borderRadius: "12px" }}
                  zoomControl={false}
                  scrollWheelZoom={true}
                >
                  <TileLayer url={TEGELS} attribution={TEGELS_BRON} />
                  <TileLayer url={TEGELS_NAMEN} />
                  <MapFlyTo center={pinLocation} trigger={recenterTrigger} />
                  <Marker 
                    position={pinLocation || userLocation}
                    draggable={true}
                    eventHandlers={{
                      dragstart: () => { manualPin.current = true; setPinMoved(true); },
                      dragend: handlePinDragEnd,
                    }}
                  />
                  {userLocation && pinLocation && (
                    <Circle
                      center={userLocation}
                      radius={userAccuracy ?? 30}
                      pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.3, weight: 1 }}
                    />
                  )}
                </MapContainer>
                <button
                  className="recenter-btn"
                  onClick={handleRecenter}
                  data-testid="recenter-location-btn"
                  title="Terug naar mijn locatie"
                >
                  <span className="recenter-icon">◎</span>
                </button>
                </>
              ) : (
                <div className="no-location">
                  <div className="spinner"></div>
                  <span>Locatie laden...</span>
                </div>
              )}
            </div>

            {pinAddress && (
              <div className="pin-address">
                <span className="pin-address-icon">📍</span>
                <span className="pin-address-text">{pinAddress}</span>
              </div>
            )}
            
            {/* Voor welke rijrichting dit geldt.
                ----------------------------------------------------------
                Alleen als we een koers hebben. Zonder koers is "de kant waar
                ik rijd" een lege uitspraak, en dan blijft het zoals het was:
                geen richting, dus geldig voor iedereen.

                Tot nu toe werd de koers zonder meer opgeslagen als de richting
                waar de melding voor gold. Wie een flitser aan de overkant zag
                staan kon dat niet zeggen. Op een snelweg liggen die twee
                rijbanen mediaan achtentwintig meter uit elkaar — binnen elke
                straal die deze app kent, dus daar is het verschil alles.

                Drie antwoorden, want twee zijn er te weinig: een mobiele
                controle op een straat zonder middenberm geldt beide kanten op,
                en die dwingen te kiezen levert een onjuist "niet voor jou". */}
            {!pinMoved && userHeading !== null && (
              <div className="direction-choice" data-testid="direction-choice">
                <div className="direction-label">Geldt voor</div>
                <div className="direction-opts" role="radiogroup" aria-label="Geldt voor">
                  {[
                    ["mine", `Mijn kant (${compassName(userHeading)})`],
                    ["opposite", `De andere kant (${compassName(opposite(userHeading))})`],
                    ["both", "Beide richtingen"],
                  ].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={direction === id}
                      className={`direction-opt ${direction === id ? "on" : ""}`}
                      onClick={() => setDirection(id)}
                      data-testid={`direction-${id}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="selected-summary">
              <span className="summary-type">
                {TYPE_BY_ID(allTypes, selectedType)?.name}
              </span>
              {selectedSubtype && selectedSubtype !== 'algemeen' && (
                <span className="summary-subtype">
                  {CONTROL_TYPES.find(c => c.id === selectedSubtype)?.emoji} {CONTROL_TYPES.find(c => c.id === selectedSubtype)?.name}
                </span>
              )}
            </div>
          </div>
          
          <button 
            className={`submit-report-btn ${isSubmitting ? 'submitting' : ''}`}
            onClick={handleSubmit}
            disabled={isSubmitting || (!pinLocation && !userLocation)}
          >
            {isSubmitting ? (
              <>
                <div className="btn-spinner"></div>
                <span>Verzenden...</span>
              </>
            ) : (
              <>
                <span className="submit-icon">📍</span>
                <span>MELD HIER</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// KAART TAB - Map View
// ============================================================
const KaartTab = ({ meldingen, mapPins, hotspots, userLocation, loading, selectedReport,
                    onReportClick, filters, setFilters }) => {
  /* Wat je zojuist aantikte. Eén melding, of niets.

     Bewust geen ballon meer. Die stond hier wél, en hij was onleesbaar: de
     tekst stond op --ink (bijna wit) terwijl Leaflets eigen omhulsel zijn
     standaard wítte achtergrond hield, want die is nooit overschreven. Wit op
     wit, en niemand die het merkte omdat het alleen te zien is als je een
     speld aantikt.

     Maar dat repareren was niet genoeg. Een ballon van 180 px hoog midden op
     een telefoonscherm bedekt precies de spelden eromheen — juist datgene
     waarvoor je op de kaart kijkt. Een kaartje onderaan laat de kaart heel,
     staat altijd op dezelfde plek, en heeft ruimte voor de regel die ertoe
     doet plus de weg naar alles wat we verder weten. */
  const [gekozen, setGekozen] = useState(null);
  const [gekozenPatroon, setGekozenPatroon] = useState(null);
  const [mapView, setMapView] = useState("meldingen");
  const [mapReady, setMapReady] = useState(false);

  /* Everything that is active, from /api/map — see useMapPins, which now lives
     one level up because the small map above the board needs the same answer.
     `meldingen` stays as the fallback for the moment before the first response
     lands, so the map is never emptier than the board it was opened from. */
  const allPins = mapPins ?? meldingen;

  /* Welk type de kaart tekent, en null is alles.

     Dezelfde woordenschat als het bord — typeChoices uit boardFilter.js, met
     dezelfde ordening en dezelfde belofte: het getal op een knop is het aantal
     spelden dat je krijgt. De Android-kaart doet ditzelfde met hetzelfde
     bedieningselement; twee kaarten in twee apps die "type" anders uitleggen
     is precies de scheur die de bordregel deze week ook had. */
  const mapTypes = filters.types;
  const setMapTypes = (next) => setFilters(previous => ({
    ...previous, types: typeof next === "function" ? next(previous.types) : next,
  }));
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const mapRang = useCallback((id) => TONE_RANK[authorityMark(id, id).tone], []);
  const mapTypeChoices = useMemo(() => typeChoices(allPins, [], mapRang),
                                 [allPins, mapRang]);
  const mapItems = useMemo(() => pinsOfTypes(allPins, mapTypes), [allPins, mapTypes]);

  // Center on selected report if provided, otherwise user location
  const center = selectedReport 
    ? [selectedReport.lat, selectedReport.lon]
    : (userLocation || [52.3676, 4.9041]);
  
  // Zoom closer when a report is selected
  const zoom = selectedReport ? 15 : 12;
  // Waar de kaart wérkelijk staat, want de gebruiker mag zelf uitzoomen. Zie
  // ZoomWatcher: op landsniveau is een teken in een speld 6.226 paden die
  // niemand kan lezen.
  const [liveZoom, setLiveZoom] = useState(zoom);
  const onZoom = useCallback((z) => setLiveZoom(z), []);
  const platteSpelden = liveZoom < GLYPH_VANAF_ZOOM;

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => setMapReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="tab-content kaart-tab">
      {/* Map */}
      <div className="map-container">
        {/* Map Toggle - Positioned over map */}
        <div className="map-toggle-container">
          <div className="map-toggle">
            <button 
              className={`toggle-btn ${mapView === 'meldingen' ? 'active' : ''}`}
              onClick={() => { setMapView('meldingen'); setGekozenPatroon(null); }}
              aria-pressed={mapView === 'meldingen'}
              data-testid="toggle-meldingen"
            >
              <span className="toggle-text">Actueel</span>
            </button>
            <button
              className={`toggle-btn ${mapView === 'intelligence' ? 'active' : ''}`}
              onClick={() => { setMapView('intelligence'); setGekozen(null); }}
              aria-pressed={mapView === 'intelligence'}
              data-testid="toggle-intelligence"
            >
              <span className="toggle-text">Patronen</span>
            </button>
          </div>
        </div>

        {/* Het typefilter. Meerkeuze, en dat is een omkering van wat hier
            stond: één type kiezen verving het vorige.

            De reden staat in de melding die hem omgooide: "alle icoontjes
            kunnen voor ruis zorgen, misschien wil een gebruiker geen
            wegwerkzaamheden zien". Dat is het uitzetten van één soort, niet
            het kiezen van één — en met 846 rijstroken en 642 werkzaamheden op
            2082 spelden is dat ook precies waar de ruis zit.

            Alleen bij de meldingen: een hittekaart filter je niet op type. */}
        {mapView === 'meldingen' && mapTypeChoices.length > 0 && (
          <div className="map-filter" data-testid="map-filter">
            <div className="map-filter-row">
              <button className={`feed-map-filter ${mapTypes.length ? "on" : ""}`}
                style={{ position: "static" }}
                onClick={() => setTypeMenuOpen(v => !v)}
                aria-expanded={typeMenuOpen} aria-haspopup="menu"
                aria-label="Filteren op soort" data-testid="map-type-btn">
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M2.5 3.5h11L9.2 8.6v4.2l-2.4 1.2V8.6z" />
                </svg>
                {mapTypes.length > 0 && <b>{mapTypes.length}</b>}
              </button>
              {mapTypes.length > 0 && (
                <button
                  className="map-pill"
                  onClick={() => setMapTypes([])}
                  data-testid="map-type-clear"
                >
                  Alles tonen <span aria-hidden="true">✕</span>
                </button>
              )}
              {/* Wat je nu ziet, in het aantal. Dit is de controle op de
                  belofte: het getal in het menu en het getal hier horen
                  hetzelfde te zijn. */}
              <span className="map-pill quiet">
                {mapTypes.length
                  ? `${mapItems.length} van ${allPins.length}`
                  : `${allPins.length} op de kaart`}
              </span>
            </div>

            {typeMenuOpen && (
              <div className="map-filter-menu" role="menu">
                <TypeFilterMenu options={mapTypeChoices} selected={mapTypes}
                  total={allPins.length} onChange={setMapTypes} />
              </div>
            )}
          </div>
        )}

        {!mapReady ? (
          <MapSpinner />
        ) : (
          <MapContainer
            center={center}
            zoom={zoom}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
          >
            <TileLayer url={TEGELS} attribution={TEGELS_BRON} />
            <TileLayer url={TEGELS_NAMEN} />
            <MapCenterUpdater center={center} zoom={zoom} />
            <ZoomWatcher onZoom={onZoom} />
            
            {/* User Location */}
            {userLocation && (
              <>
                <Marker position={userLocation} icon={userLocationIcon} zIndexOffset={2000} />
                <Circle
                  center={userLocation}
                  radius={100}
                  pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.15, weight: 0 }}
                />
              </>
            )}
            
            {/* Selected report highlight circle */}
            {selectedReport && (
              <Circle
                center={[selectedReport.lat, selectedReport.lon]}
                radius={150}
                pathOptions={{ 
                  color: "#F97316", 
                  fillColor: "#F97316", 
                  fillOpacity: 0.2, 
                  weight: 3,
                  dashArray: "5, 5"
                }}
              />
            )}
            
            {mapView === 'meldingen' ? (
              // Meldingen markers
              mapItems.map(melding => {
                const isPossiblyGone = melding.lifecycle_status === 'possibly_ended' || melding.lifecycle_status === 'possibly_removed';
                const isSelected = selectedReport && selectedReport.id === melding.id;
                return (
                  <Marker
                    key={melding.id}
                    position={[melding.lat, melding.lon]}
                    icon={serviceDot(melding, {
                      selected: isSelected,
                      faded: isPossiblyGone,
                      // Een geselecteerde speld houdt zijn teken, ook
                      // uitgezoomd: dat is de enige die je op dat moment leest.
                      plain: platteSpelden && !isSelected,
                    })}
                    zIndexOffset={isSelected ? 1000 : 0}
                    eventHandlers={{ click: () => setGekozen(melding) }}
                  />
                );
              })
            ) : (
              // Historische patronen: een zachte zone en bescheiden marker.
              hotspots.map(hotspot => (
                <React.Fragment key={hotspot.id}>
                  <Circle
                    center={[hotspot.lat, hotspot.lon]}
                    radius={Math.min(hotspot.radius_meters || 300, 500)}
                    pathOptions={{
                      color: '#F5B942',
                      fillColor: '#F5B942',
                      fillOpacity: hotspot.relevant_now ? 0.15 : 0.09,
                      weight: 1
                    }}
                  />
                  <Marker
                    position={[hotspot.lat, hotspot.lon]}
                    icon={createPatternIcon(Boolean(hotspot.relevant_now))}
                    eventHandlers={{ click: () => setGekozenPatroon(hotspot) }}
                  />
                </React.Fragment>
              ))
            )}
          </MapContainer>
        )}
      </div>
      
      {/* Same key as the map above the feed, so the two read as one system.
          The count gets no colour swatch: the dots are coloured per service,
          and one swatch cannot stand for eight. */}
      <div className="feed-map-legend map-legend-v3">
        {mapView === 'meldingen' ? (
          <>
            <span>{mapItems.length} actief</span>
            <span><i className="lg-you" />Jij</span>
          </>
        ) : (
          <>
            <span><i className="lg-pattern" />{hotspots.length} {hotspots.length === 1 ? 'patroon' : 'patronen'}</span>
            <span>afgelopen 30 dagen</span>
          </>
        )}
      </div>

      {/* Het kaartje van de aangetikte speld.

          Drie regels en één knop, en verder niets. Wat er nog meer bekend is —
          betrouwbaarheid, geschiedenis, bevestigen, vrijgeven — staat al op het
          detailscherm; dat hier herhalen zou een tweede plek maken waar
          dezelfde waarheid uiteen kan lopen. */}
      {mapView === 'meldingen' && gekozen && (
        <div className="kaart-kaartje" data-testid="kaart-kaartje">
          <button
            className="kk-sluit"
            onClick={() => setGekozen(null)}
            aria-label="Sluiten"
          >×</button>
          <button
            className="kk-lijf"
            onClick={() => { onReportClick && onReportClick(gekozen); setGekozen(null); }}
          >
            <span className="kk-teken">
              <span dangerouslySetInnerHTML={{ __html: markerHtml({
                shape: glyphShape(glyphFor(gekozen.authority,
                        authorityMark(gekozen.authority, gekozen.authority_name).tone)),
                colour: serviceColour(gekozen.authority, gekozen.authority_name),
                size: 30,
              }) }} />
            </span>
            <span className="kk-tekst">
              <span className="kk-soort">{gekozen.authority_name || cardTitle(gekozen)}</span>
              <span className="kk-waar">{gekozen.address || gekozen.city}</span>
              <span className="kk-meta">
                {timeAgo(gekozen.timestamp)}
                {gekozen.confirmations > 0 && ` · ${gekozen.confirmations}× bevestigd`}
                {(gekozen.lifecycle_status === 'possibly_ended'
                  || gekozen.lifecycle_status === 'possibly_removed') && ' · mogelijk weg'}
                {/* En hoeveel mensen al zeiden dat hij weg is. Dit ontbrak
                    hier, en het ontbrak ook in /api/map — dus de kaart kon
                    niet tonen wat het bord ernaast wél toonde. */}
                {vrijgegevenLabel(gekozen.gone_signals_count,
                                  gekozen.gone_signals_needed)
                  && ` · ${vrijgegevenLabel(gekozen.gone_signals_count,
                                            gekozen.gone_signals_needed)}`}
              </span>
            </span>
            <span className="kk-pijl" aria-hidden="true">›</span>
          </button>
        </div>
      )}

      {mapView === 'intelligence' && gekozenPatroon && (
        <article className="pattern-card" data-testid="pattern-card">
          <button
            className="kk-sluit"
            onClick={() => setGekozenPatroon(null)}
            aria-label="Patroon sluiten"
          >×</button>
          <div className="pattern-card-kicker">
            {gekozenPatroon.relevant_now ? 'Rond dit tijdstip' : 'Terugkerend patroon'}
          </div>
          <h3>{gekozenPatroon.relevant_now
            ? 'Hier wordt rond dit moment vaker gecontroleerd'
            : 'Hier wordt vaker gecontroleerd'}</h3>
          <p className="pattern-card-location">
            {shortenAddress(gekozenPatroon.address, gekozenPatroon.city)}
          </p>
          <div className="pattern-card-facts">
            <span>{gekozenPatroon.report_count || gekozenPatroon.total_spotted || 0}× gemeld</span>
            <span>{gekozenPatroon.distinct_days || 0} verschillende dagen</span>
            {(gekozenPatroon.pattern_days_display || gekozenPatroon.pattern_time_range) && (
              <span>Meestal {[gekozenPatroon.pattern_days_display, gekozenPatroon.pattern_time_range].filter(Boolean).join(' · ')}</span>
            )}
          </div>
          <div className="pattern-card-evidence">
            <i aria-hidden="true" />
            {gekozenPatroon.evidence_label || 'Beginnende aanwijzing'}
          </div>
          <p className="pattern-card-note">Gebaseerd op eerdere meldingen, geen live controle.</p>
        </article>
      )}

      {mapView === 'intelligence' && !gekozenPatroon && hotspots.length === 0 && !loading && (
        <div className="pattern-empty" data-testid="pattern-empty">
          <strong>Nog geen betrouwbaar patroon in de buurt</strong>
          <span>Een patroon verschijnt na meldingen op minimaal drie verschillende dagen.</span>
        </div>
      )}
    </div>
  );
};

// ============================================================
// FEED — het tweede bord
// ============================================================
// Het onderscheid met het eerste is het hele ontwerp:
//
//     BORD   dingen die er staan.    Jij kunt ze bevestigen of vrijgeven.
//     FEED   dingen die gebeuren.    Je kunt ze alleen weten.
//
// Daarom staat hier geen enkele knop op een regel. Er valt niets te bevestigen
// aan een alarmering en niets vrij te geven aan een weerbericht, en een knop die
// suggereert van wel zou liegen.
//
// En daarom is de as tijd en geen afstand. Het bord beantwoordt "wat komt er
// aan"; dit beantwoordt "wat gebeurt er", en dan is "wanneer" de ordening.
//
// Dit scherm bestond alleen op Android — gevraagd: waarom heeft de mini app de
// Feed niet. Zelfde endpoint, zelfde tijdvakken (feedBuckets.js is regel voor
// regel FeedBuckets.kt), zelfde woorden.

// De kleuren van de Feed. Welke toon een regel krijgt staat in feedLook.js —
// dezelfde tabel als FeedLook.kt — en welke kleur die toon is, staat hier, bij
// de rest van het palet.
const FEED_TINT = {
  sit: SERVICE_COLOUR.sit,
  werk: SERVICE_COLOUR.werk,
  weer: "#7fb6c6",
  evt: "#c084fc",
  // Voorbij, en alles wat we niet thuis kunnen brengen.
  done: "#22c55e",
};

const feedTint = (item) => FEED_TINT[feedTone(item)] || FEED_TINT.done;

const feedDistance = (m) =>
  m == null ? null : m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${m} m`;

/**
 * Het kaartje met de routelijn.
 *
 * Stil, net als het kaartje boven het bord: geen slepen, geen zoomen, geen
 * draaien. Dit is een plaatje om naar te kijken — wie de kaart wil gebruiken
 * heeft daar een tabblad voor, en een half-interactieve kaart midden in een
 * scrollende lijst vecht met het scrollen (zie de opmerking bij FeedMap).
 *
 * Het poortje van 100 ms is hetzelfde als bij FeedMap en geen willekeur:
 * `fitBounds` vóórdat het omhulsel hoogte heeft geeft een onzinnig zoomniveau
 * en soms helemaal geen tegels.
 */
const RitKaart = ({ lijn, meldingen }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  if (!lijn || lijn.length < 2) return null;

  return (
    <div className="feed-map" data-testid="rit-kaart">
      {ready ? (
        <MapContainer
          center={lijn[0]}
          zoom={8}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          keyboard={false}
          attributionControl={false}
        >
          <TileLayer url={TEGELS} attribution={TEGELS_BRON} />
          <TileLayer url={TEGELS_NAMEN} />
          <KaartOpLijn positions={lijn} />
          {/* Blauw en niet groen of rood: in dit bestand heeft elke kleur één
              betekenis, en groen is "bevestigd, van ons" terwijl het rose van
              --svc-flits al de trajectlijn is. Dikker dan die trajectlijn
              (2,5) zodat de twee lijnsoorten op één kaart niet door elkaar
              lopen. */}
          <Polyline positions={lijn}
                    pathOptions={{ color: "#2f6fe4", weight: 4, opacity: 0.85, lineCap: "round" }} />
          <Marker position={lijn[0]} icon={ritStartIcon} />
          <Marker position={lijn[lijn.length - 1]} icon={ritEindIcon} />
          {meldingen.filter(m => m.lat != null && m.lon != null).map(m => (
            <Marker key={m.id} position={[m.lat, m.lon]}
                    icon={serviceDot(m, { compact: true })} />
          ))}
        </MapContainer>
      ) : (
        <MapSpinner />
      )}
    </div>
  );
};

/**
 * Het Rit-tabblad: typ een bestemming, zie wat erop ligt.
 *
 * ------------------------------------------------------------------
 * Gelijk aan het ritscherm op Android, en dat is hier geen stijlkeuze: de
 * kilometer, het ritme van het zoekveld en de dekkingsregel komen alle drie uit
 * dezelfde bron, en wie in de mini-app een ander getal of een ander ritme
 * krijgt dan in de app, gebruikt twee producten. Zie ritTempo.js en
 * RitScreen.kt.
 *
 * **De kilometerkolom en niet "400 m".** `feedDistance` zou onder een kilometer
 * naar meters omschakelen; dit scherm houdt één decimaal aan, ook bij 0,4 —
 * zodat de getallen in de kolom onder elkaar uitlijnen en zodat het dezelfde
 * tekst is als op Android.
 *
 * **Geen gebruiker in het verzoek.** Deze component krijgt met opzet geen
 * `userId` en geen accountgegevens: wat je niet hebt, plak je ook niet per
 * ongeluk bij een verzoek. De drie `/api/route/*`-aanroepen sturen uitsluitend
 * de velden die de server vraagt, zonder headers en zonder `withCredentials`.
 * De sessiecookie van de accountlaag reist buiten ons om mee omdat hij op
 * `path="/"` staat; `route_routes.py` leest hem niet, en dat is daar met een
 * test vastgelegd.
 *
 * **En in de catch geen foutobject.** `console.error("...", e)` lijkt "alleen
 * de fout loggen" en is het niet: een axios-fout draagt `e.config.data` en
 * `e.config.url`, dus daarmee staat de hele routebody met coördinaten in de
 * console van het toestel. Zelfde regel als op de server, waar
 * `route_routes.py` alleen de sóórt fout logt.
 */
const RitTab = ({ userLocation }) => {
  // `types` en niet `authorities`. Dat onderscheid kostte een scherm vol
  // "Onbekend": `authorities` is hierboven gefilterd op
  // `category === 'authority'` en kent daarmee 12 van de 23 types. De elf die
  // wegvallen zijn de flitsers, de trajectcontroles, de wegwerkzaamheden en de
  // situaties — zo ongeveer alles wat op een rit staat. Zie ritRegel.js.
  const { types } = useConfig();

  const [invoer, setInvoer] = useState("");
  const [suggesties, setSuggesties] = useState([]);
  const [zoekt, setZoekt] = useState(false);
  const [bestemming, setBestemming] = useState(null);
  const [rekent, setRekent] = useState(false);
  const [rit, setRit] = useState(null);
  const [fout, setFout] = useState(null);

  // Twee tellers en niet één. Met één exemplaar markeert het starten van een
  // routeberekening een lopend suggestieverzoek als achterhaald, en dan blijft
  // de suggestielijst willekeurig leeg. Zie latestOnly.test.js.
  const claimZoek = useRef(latestOnly()).current;
  const claimRit = useRef(latestOnly()).current;

  // De áchterste rand: even niets doen en dan het laatste wat er staat. Niet
  // moetOphalen uit ophaalTempo.js — dat is een throttle op de vóórste rand en
  // zou "Co" wegsturen en "Coolsingel" nooit. Zie de kop van ritTempo.js.
  useEffect(() => {
    if (!magZoeken(invoer)) {
      setSuggesties([]);
      setZoekt(false);
      return undefined;
    }
    const vraag = invoer.trim();
    const t = setTimeout(async () => {
      const stale = claimZoek();
      setZoekt(true);
      try {
        const res = await axios.post(`${API}/route/adres/zoek`, { q: vraag }, { timeout: 8000 });
        if (stale()) return;
        setSuggesties(res.data.suggesties || []);
      } catch (e) {
        if (stale()) return;
        // Alleen de sóórt, nooit het object: zie de kop.
        setSuggesties([]);
      } finally {
        if (!stale()) setZoekt(false);
      }
    }, RUST_MS);
    return () => clearTimeout(t);
  }, [invoer, claimZoek]);

  const kies = useCallback(async (suggestie) => {
    setSuggesties([]);
    setInvoer(suggestie.naam);
    setFout(null);
    if (!userLocation) {
      setFout("Zonder je eigen positie kan er geen rit berekend worden.");
      return;
    }
    const stale = claimRit();
    setRekent(true);
    setRit(null);
    try {
      const punt = await axios.post(`${API}/route/adres/punt`, { id: suggestie.id }, { timeout: 8000 });
      if (stale()) return;
      const res = await axios.post(`${API}/route/vooruit`, {
        van_lat: userLocation[0], van_lon: userLocation[1],
        naar_lat: punt.data.lat, naar_lon: punt.data.lon,
      }, { timeout: 20000 });
      if (stale()) return;
      setBestemming(punt.data.naam || suggestie.naam);
      setRit(res.data);
    } catch (e) {
      if (stale()) return;
      // De zin van de server als die er is, anders een eigen zin. Het
      // foutobject zelf gaat nergens heen.
      setFout(e?.response?.data?.detail || "De rit kon niet berekend worden.");
    } finally {
      if (!stale()) setRekent(false);
    }
  }, [userLocation, claimRit]);

  const wis = useCallback(() => {
    setInvoer("");
    setSuggesties([]);
    setBestemming(null);
    setRit(null);
    setFout(null);
  }, []);

  // Memoiseren op de vorm-string: een nieuwe array bij elke hertekening is een
  // nieuwe identiteit, en dan past de kaart zich bij elke toetsaanslag in het
  // adresveld opnieuw in. lijnUitRit leest vorm_decimalen uit het antwoord en
  // zet de Nederland-vangrail erachter; niet zelf decoderen.
  const lijn = useMemo(() => lijnUitRit(rit), [rit]);

  // Onder de negentig procent hoort erbij te staan hoeveel van de route we
  // niet kennen. Zonder deze regel leest "niets gemeld" als "er staat niets",
  // ook als we de helft van de weg niet in onze wegvakken hebben — en dat is
  // het verschil tussen een gerust gevoel en een vals gerust gevoel. Boven de
  // negentig is het ruis die twijfel zaait over een antwoord dat klopt.
  const dekkingsregel = rit && rit.dekking < 0.9
    ? `Van deze rit kennen we ${Math.round(rit.dekking * 100)}% van de wegen. `
      + "Op de rest kunnen meldingen staan die we niet zien."
    : null;

  return (
    <div className="tab-content rit-tab" data-testid="rit-tab">
      <div className="feed-head">
        <div className="feed-head-text">
          <span className="feed-head-over">RIT</span>
          <h2 className="feed-head-title">{bestemming || "Waar ga je heen?"}</h2>
          <p className="feed-head-sub">
            {rit
              ? `${(rit.lengte_m / 1000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} km `
                + `· ${Math.round(rit.duur_s / 60)} min · ${rit.meldingen.length} meldingen`
              : "Typ een bestemming en zie wat er onderweg staat."}
          </p>
        </div>
      </div>

      <div className="svc-bar rit-zoek">
        <div className="address-search">
          <input
            type="text"
            className="address-input"
            placeholder="Adres, straat of plaats..."
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
            data-testid="rit-adres-input"
          />
          <button type="button" className="address-search-btn"
                  onClick={wis} disabled={!invoer}
                  aria-label="Wissen" data-testid="rit-wis">
            {zoekt ? "..." : "×"}
          </button>
        </div>
        {suggesties.length > 0 && (
          <>
            <div className="svc-menu-scrim" onClick={() => setSuggesties([])} />
            <div className="svc-menu" data-testid="rit-suggesties">
              {suggesties.map(s => (
                <button type="button" key={s.id} className="svc-menu-row"
                        onClick={() => kies(s)}>
                  <span className="svc-menu-name">{s.naam}</span>
                  <span className="svc-menu-count">{s.soort}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {fout && <p className="board-report-warning" data-testid="rit-fout">{fout}</p>}

      {rekent && <div className="map-loading"><div className="spinner" /><span>Rit berekenen...</span></div>}

      {rit && (
        <>
          <RitKaart lijn={lijn} meldingen={rit.meldingen} />

          {dekkingsregel && (
            <p className="board-report-warning" data-testid="rit-dekking">{dekkingsregel}</p>
          )}

          {rit.meldingen.length === 0 ? (
            <div className="board-clear" data-testid="rit-leeg">
              <span className="clear-ring">&#10003;</span>
              <span>Niets gemeld op deze rit.</span>
            </div>
          ) : (
            <div className="rit-lijst">
              {rit.meldingen.map(m => {
                const weg = roadLine({
                  // Externe items (NDW, OSM) hebben geen address maar wel een
                  // road_name, en dat zijn juist de meldingen op een snelweg —
                  // de meeste op een rit. Zonder deze terugval is de onderste
                  // regel precies daar leeg.
                  address: m.address || m.road_name,
                  city: m.city,
                });
                // Zonder authority_name, en dat is geen slordigheid: dat veld
                // staat niet op de witte lijst van /api/route/vooruit (zie
                // NAAR_BUITEN in route_routes.py) omdat geen van de twee
                // ritschermen het nodig heeft. authorityMark leest het alleen
                // als de dienst onbekend is, dus het meegeven zou een veld
                // suggereren dat er niet is.
                const kleur = serviceColour(m.authority);
                return (
                  <div className="board-report-card rit-rij" key={m.id}>
                    <span className="rit-km">
                      {ritKm(m.route_m)}
                      <span className="rit-km-eenheid">KM</span>
                    </span>
                    {weg.shield && (
                      <span className={`road-shield shield-${weg.shield.kind}`}>
                        {weg.shield.label}
                      </span>
                    )}
                    <span className="board-report-symbol" style={{ background: kleur }} aria-hidden="true">
                      <svg viewBox="0 0 16 16" dangerouslySetInnerHTML={{
                        __html: glyphShape(glyphFor(m.authority,
                          authorityMark(m.authority).tone)),
                      }} />
                    </span>
                    <span className="board-report-copy">
                      {/* Niet cardTitle(): die valt op een rúwe melding uit
                          /api/route/vooruit terug op authority_name, dat daar
                          niet in zit, en React rendert undefined als niets —
                          een lege vette regel zonder fout. En niet
                          getAuthority(): dat is de verkeerde catalogus en gaf
                          een scherm vol "Onbekend". Zie ritRegel.js. */}
                      <strong>{ritTitel(types, m)}</strong>
                      {ritOnder(m, weg) && (
                        <span className="board-report-place">{ritOnder(m, weg)}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {!rit && !rekent && !userLocation && (
        <p className="board-report-place" data-testid="rit-geen-positie">
          Wacht op je positie...
        </p>
      )}
    </div>
  );
};

const FeedTab = ({ userLocation, onOpenSettings }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const q = userLocation ? `?lat=${userLocation[0]}&lon=${userLocation[1]}` : "";
        // Met een grens aan het wachten. Zonder die grens blijft een verzoek
        // dat niet terugkomt eeuwig openstaan, blijft `loading` waar en staat
        // er "Laden..." tot je de app afsluit — dat was precies wat er
        // gebeurde toen de server er honderd seconden over deed. Een fout
        // tonen is eerlijker dan een streepje dat blijft draaien.
        const res = await axios.get(`${API}/feed${q}`, { timeout: 20000 });
        if (cancelled) return;
        setItems(res.data.items || []);
        setError(null);
      } catch (e) {
        // Een mislukte lezing gooit niet weg wat er al staat — dezelfde regel
        // als op het bord.
        if (!cancelled) setError(items.length ? null : "Kon de feed niet laden.");
      }
      if (!cancelled) setLoading(false);
    };
    load();
    // Rustiger dan het bord: dit gaat over de regio en niet over de volgende
    // kilometer.
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  const groups = group(items);

  return (
    <div className="tab-content feed-tab" data-testid="feed-tab">
      <div className="feed-header">
        <div>
          <div className="feed-title">Feed</div>
          <div className="feed-head-sub">
            {userLocation
              ? "Wat er om je heen gebeurt"
              // Zonder locatie blijft alleen het landelijke over. Dat is geen
              // fout, maar het scherm hoort niet te doen alsof dit alles is.
              : "Alleen landelijke berichten — locatie staat uit"}
          </div>
        </div>
        <button
          className="feed-gear"
          onClick={onOpenSettings}
          aria-label="Instellingen"
          data-testid="feed-settings"
        >
          ⚙
        </button>
      </div>

      {loading && items.length === 0 && (
        <div className="feed-note">Laden…</div>
      )}

      {!loading && error && <div className="feed-note">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="empty-state" data-testid="feed-empty">
          <span className="clear-ring">✓</span>
          <p>Rustig in de buurt</p>
          <p className="hint">
            Hier komen ongevallen, weerwaarschuwingen en aangekondigde
            afsluitingen te staan. Wat er nú op de weg staat, staat op het bord.
          </p>
        </div>
      )}

      {groups.map(({ id, label, rows }) => (
        <div key={id} className="feed-bucket">
          <div className="feed-bucket-head">
            <span>{label}</span>
            <i />
          </div>
          {rows.map(item => {
            const tint = feedTint(item);
            const urgent = feedUrgent(item);
            return (
              <div
                key={item.id}
                className={`feed-item ${urgent ? "urgent" : ""}`}
                style={urgent ? { borderColor: `${tint}6b` } : undefined}
                data-testid="feed-item"
              >
                <span
                  className="feed-item-mark"
                  style={{ color: tint, background: `${tint}29` }}
                  aria-hidden="true"
                >
                  {feedMark(item)}
                </span>
                <div className="feed-item-body">
                  <div className="feed-item-title">{item.title}</div>
                  {item.body && <div className="feed-item-text">{item.body}</div>}
                  {/* De gemeten getallen op hun eigen regel: "2 rijstroken dicht
                      · +18 min · 27 km/u". Leeg als er niets gemeten is, en dan
                      staat hij er niet — een lege regel is geen regel. */}
                  {item.facts && (
                    <div className="feed-item-facts" style={{ color: tint }}>
                      {item.facts}
                    </div>
                  )}
                  <div className="feed-item-meta">
                    {/* Hier stond de bron, in hoofdletters. Sinds de bron
                        bewust binnenblijft stond op elke regel hetzelfde woord —
                        "MELDRADAR", negen keer onder elkaar. Dat is geen
                        informatie, dat is behang. */}
                    {item.at && <span className="feed-item-when">{timeAgo(item.at)}</span>}
                    {feedDistance(item.distance_m) && (
                      <span className="feed-item-far">{feedDistance(item.distance_m)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * "Was hij er nog?" — de vraag die alleen op het moment zelf beantwoord kan
 * worden.
 *
 * Stond in WatchTab, en liep dus alleen zolang je naar dat tabblad keek. Dat is
 * precies het verkeerde moment: wie langs een controle rijdt heeft de app niet
 * open op een instellingenscherm. Nu draait hij in de schil, boven welk tabblad
 * dan ook — dezelfde plek als DriveService.pendingVerification op Android.
 */
const ProximityWatch = ({ userLocation, userId, watchEnabled, radius }) => {
  const [nearbyReports, setNearbyReports] = useState([]);
  const [verifyingReport, setVerifyingReport] = useState(null);

  useEffect(() => {
    if (!watchEnabled || !userLocation || !userId) return;

    const checkProximity = async () => {
      try {
        const response = await axios.post(`${API}/watch/proximity-check`, {
          user_id: userId,
          lat: userLocation[0],
          lon: userLocation[1],
          radius: radius,
        });
        if (response.data.nearby_reports.length > 0) {
          setNearbyReports(response.data.nearby_reports);
        }
      } catch (error) {
        console.error('Proximity check failed:', error);
      }
    };

    checkProximity();
    const interval = setInterval(checkProximity, 30000);
    return () => clearInterval(interval);
  }, [watchEnabled, userLocation, userId, radius]);

  const handleVerify = async (reportId, stillPresent) => {
    if (!userLocation || !userId) return;
    setVerifyingReport(reportId);
    try {
      const response = await axios.post(`${API}/watch/verify`, {
        report_id: reportId,
        user_id: userId,
        lat: userLocation[0],
        lon: userLocation[1],
        still_present: stillPresent,
      });
      if (response.data.success) {
        setNearbyReports(prev => prev.filter(r => r.report_id !== reportId));
      }
    } catch (error) {
      console.error('Verification failed:', error);
    }
    setVerifyingReport(null);
  };

  const dismissReport = (reportId) =>
    setNearbyReports(prev => prev.filter(r => r.report_id !== reportId));

  if (!watchEnabled || nearbyReports.length === 0) return null;

  return (
    <div className="proximity-alert" data-testid="proximity-alert">
      <div className="proximity-header">
        <span className="proximity-icon">📍</span>
        <span className="proximity-title">Controle in de buurt!</span>
      </div>
      {nearbyReports.slice(0, 3).map(report => (
        <div key={report.report_id} className="proximity-card">
          <div className="proximity-info">
            <span className="proximity-type">
              {report.authority_emoji} {report.control_name}
            </span>
            <span className="proximity-distance">{report.distance_meters}m</span>
          </div>
          <div className="proximity-address">{report.address || report.city}</div>
          <div className="proximity-question">Controle nog aanwezig?</div>
          <div className="proximity-actions">
            <button
              className="proximity-btn yes"
              onClick={() => handleVerify(report.report_id, true)}
              disabled={verifyingReport === report.report_id}
            >
              {verifyingReport === report.report_id ? '...' : '✅ Ja'}
            </button>
            <button
              className="proximity-btn no"
              onClick={() => handleVerify(report.report_id, false)}
              disabled={verifyingReport === report.report_id}
            >
              {verifyingReport === report.report_id ? '...' : '❌ Nee'}
            </button>
            <button
              className="proximity-btn skip"
              onClick={() => dismissReport(report.report_id)}
            >
              ⏭
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// WATCH ALERTS — nu een blok in de instellingen, geen tabblad
// ============================================================
const WatchTab = ({ userLocation, userId, watchEnabled, setWatchEnabled, radius, setRadius, selectedAuthorities, setSelectedAuthorities }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load saved settings from backend on first mount
  useEffect(() => {
    if (!userId || settingsLoaded) return;
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/watch/settings/${userId}`);
        if (res.data) {
          setWatchEnabled(res.data.enabled);
          setRadius(res.data.radius || 3000);
          setSelectedAuthorities(res.data.authorities || []);
        }
      } catch (err) {
        console.warn('Could not load watch settings:', err);
      }
      setSettingsLoaded(true);
    };
    loadSettings();
  }, [userId, settingsLoaded, setWatchEnabled, setRadius, setSelectedAuthorities]);

  const toggleAuthority = (id) => {
    setSelectedAuthorities(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!userLocation) {
      alert('Locatie niet beschikbaar');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/watch/settings`, {
        user_id: userId,
        lat: userLocation[0],
        lon: userLocation[1],
        radius: radius,
        authorities: selectedAuthorities,
        enabled: watchEnabled,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save watch settings:', error);
      alert('Opslaan mislukt, probeer opnieuw');
    }
    setIsSaving(false);
  };

  return (
    <div className="tab-content watch-tab">
      {/* Als kop op het niveau van de andere blokken in dit scherm: dit is nu
          een sectie in de instellingen en geen tabblad met een eigen titel. */}
      <div className="watch-header">
        <h4>Alerts in je buurt</h4>
        <p className="watch-subtitle">Een seintje via de Telegram-bot als er iets bij je in de buurt gemeld wordt.</p>
      </div>

      {/* Enable Toggle - Improved styling */}
      <div className="watch-section toggle-section">
        <div className="toggle-row">
          <div className="toggle-label-group">
            <span className="toggle-title">Alerts</span>
            <span className="toggle-subtitle">Activeer locatie alerts</span>
          </div>
          <button 
            className={`watch-toggle ${watchEnabled ? 'on' : 'off'}`}
            onClick={() => setWatchEnabled(!watchEnabled)}
            data-testid="watch-toggle"
          >
            <span className="toggle-track">
              <span className="toggle-slider"></span>
            </span>
            <span className="toggle-status">{watchEnabled ? 'AAN' : 'UIT'}</span>
          </button>
        </div>
      </div>

      {watchEnabled && (
        <>
          {/* Radius Selection - Renamed and improved spacing */}
          <div className="watch-section">
            <h3>📍 Alert Radius</h3>
            <p className="section-hint">Selecteer het gebied rondom je locatie</p>
            <div className="radius-options">
              {RADIUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  className={`radius-btn ${radius === option.value ? 'selected' : ''}`}
                  onClick={() => setRadius(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Authority Filters */}
          <div className="watch-section">
            <h3>🚔 Filter op dienst</h3>
            <p className="section-hint">Laat leeg voor alle diensten</p>
            <div className="authority-filters">
              {AUTHORITIES.map(auth => (
                <button
                  key={auth.id}
                  className={`authority-chip ${selectedAuthorities.includes(auth.id) ? 'selected' : ''}`}
                  onClick={() => toggleAuthority(auth.id)}
                >
                  <span className="chip-emoji">{auth.emoji}</span>
                  <span className="chip-label">{auth.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Location Preview */}
          <div className="watch-section">
            <h3>📍 Jouw locatie</h3>
            <div className="location-preview-watch">
              {userLocation ? (
                <MapContainer
                  center={userLocation}
                  zoom={13}
                  style={{ height: "140px", width: "100%", borderRadius: "12px" }}
                  zoomControl={false}
                  dragging={false}
                >
                  <TileLayer url={TEGELS} attribution={TEGELS_BRON} />
                  <TileLayer url={TEGELS_NAMEN} />
                  <Marker position={userLocation} icon={userLocationIcon} zIndexOffset={2000} />
                  <Circle
                    center={userLocation}
                    radius={radius}
                    pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.1, weight: 2 }}
                  />
                </MapContainer>
              ) : (
                <div className="no-location">
                  <div className="spinner"></div>
                  <span>📍 Locatie laden...</span>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <button 
            className={`save-watch-btn ${saved ? 'saved' : ''} ${isSaving ? 'saving' : ''}`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {saved ? (
              <>✅ Opgeslagen!</>
            ) : isSaving ? (
              <>
                <div className="btn-spinner"></div>
                <span>Opslaan...</span>
              </>
            ) : (
              <>💾 Opslaan</>
            )}
          </button>
        </>
      )}

      {!watchEnabled && (
        <div className="watch-disabled-hint">
          <span className="hint-icon">💡</span>
          <p>Schakel alerts in om meldingen in je buurt te ontvangen via de Telegram bot.</p>
          <p className="hint-sub">Je ontvangt notificaties wanneer er een controle in de buurt wordt gemeld.</p>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MIJN REGIO TAB - Regional Overview
// ============================================================
const MijnRegioTab = ({ stats, hotspots, leaderboard, groupLeaderboard, hotspotTimeWindow }) => {
  const [leaderboardType, setLeaderboardType] = useState("drivers");
  
  const timeWindowLabels = {
    '24h': '24 uur',
    '7d': '7 dagen',
    '30d': '30 dagen'
  };

  return (
    <div className="tab-content regio-tab">
      <div className="regio-header">
        <h2>🌍 Regio</h2>
      </div>

      {/* Regional Stats - Improved spacing */}
      <div className="regio-stats">
        <div className="regio-stat">
          <span className="stat-icon">🔥</span>
          <span className="stat-value">{stats?.active || 0}</span>
          <span className="stat-label">Actief nu</span>
        </div>
        <div className="stat-divider"></div>
        <div className="regio-stat">
          <span className="stat-icon">📊</span>
          <span className="stat-value">{stats?.total || 0}</span>
          <span className="stat-label">Totaal</span>
        </div>
        <div className="stat-divider"></div>
        <div className="regio-stat">
          <span className="stat-icon">👥</span>
          <span className="stat-value">{stats?.reporters || 0}</span>
          <span className="stat-label">Melders</span>
        </div>
      </div>

      {/* Lokale patronen uit eerdere meldingen. */}
      <div className="regio-section hotspots-section">
        <h3>Lokale patronen <span className="hotspot-period">({timeWindowLabels[hotspotTimeWindow] || '30 dagen'})</span></h3>
        {hotspots.length === 0 ? (
          <div className="empty-hint">
            <span>📍</span>
            <p>Nog geen betrouwbaar patroon in de afgelopen {timeWindowLabels[hotspotTimeWindow] || '30 dagen'}</p>
          </div>
        ) : (
          <div className="hotspots-mini">
            {hotspots.slice(0, 3).map((hotspot, index) => {
              const sh = getRoadShield(hotspot.address, hotspot.city);
              return (
              <div key={hotspot.id} className="hotspot-mini-card">
                <span className="hotspot-rank">#{index + 1}</span>
                <span className={`road-shield shield-${sh.kind}`}>{sh.label}</span>
                <div className="hotspot-info">
                  <span className="hotspot-location">{shortenAddress(hotspot.address, hotspot.city)}</span>
                  <span className="hotspot-count">
                    {hotspot.report_count || hotspot.total_spotted || 0} meldingen · {hotspot.distinct_days || 0} dagen
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Leaderboard - Improved toggle styling */}
      <div className="regio-section leaderboard-section">
        <div className="leaderboard-header">
          <h3>🏆 Leaderboard</h3>
        </div>
        
        {/* Improved toggle */}
        <div className="leaderboard-toggle-container">
          <div className="leaderboard-toggle">
            <button 
              className={`lb-toggle-btn ${leaderboardType === 'drivers' ? 'active' : ''}`}
              onClick={() => setLeaderboardType('drivers')}
            >
              <span className="toggle-icon">👤</span>
              <span className="toggle-text">Bestuurders</span>
            </button>
            <button 
              className={`lb-toggle-btn ${leaderboardType === 'groups' ? 'active' : ''}`}
              onClick={() => setLeaderboardType('groups')}
            >
              <span className="toggle-icon">👥</span>
              <span className="toggle-text">Groepen</span>
            </button>
          </div>
        </div>

        <div className="leaderboard-list">
          {leaderboardType === 'drivers' ? (
            leaderboard.length === 0 ? (
              <div className="empty-hint">
                <span>👤</span>
                <p>Geen gebruikers</p>
              </div>
            ) : (
              leaderboard.slice(0, 5).map((user, index) => (
                <div key={user.user_id} className="leaderboard-item">
                  <span className="rank-medal">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`}
                  </span>
                  <span className="lb-name">@{user.username}</span>
                  <span className="lb-score">
                    <span className="score-icon">🔥</span>
                    <span className="score-value">{user.total_reports}</span>
                  </span>
                </div>
              ))
            )
          ) : (
            groupLeaderboard.length === 0 ? (
              <div className="empty-hint">
                <span>👥</span>
                <p>Geen groepen</p>
              </div>
            ) : (
              groupLeaderboard.slice(0, 5).map((group, index) => (
                <div key={group.group_id} className="leaderboard-item">
                  <span className="rank-medal">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`}
                  </span>
                  <span className="lb-name">{group.group_title}</span>
                  <span className="lb-score">
                    <span className="score-icon">🔥</span>
                    <span className="score-value">{group.total_contributions}</span>
                  </span>
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ADMIN PANEL (only visible to configured admins)
// ============================================================

// Telegram Mini App initData — the signed string used to prove admin identity
// server-side. Empty outside Telegram (admin endpoints then return 403).
const getInitData = () => {
  try {
    return window.Telegram?.WebApp?.initData || "";
  } catch (e) {
    return "";
  }
};

const adminConfig = () => ({ headers: { "X-Telegram-Init-Data": getInitData() } });

// Hoe ver "dezelfde plek" reikt bij het nakijken van een melding. Ruim genoeg
// om een tweede document te vinden dat net buiten de samenvoegafstand viel
// (die is een fractie hiervan), krap genoeg om de buurt niet mee te nemen.
const SPOT_RADIUS_M = 150;

const hhmm = (h, m) => `${String(h ?? 0).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`;

const WEEKDAYS = [
  { id: "mon", label: "Ma" }, { id: "tue", label: "Di" }, { id: "wed", label: "Wo" },
  { id: "thu", label: "Do" }, { id: "fri", label: "Vr" }, { id: "sat", label: "Za" },
  { id: "sun", label: "Zo" },
];

const AdminSection = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [forecastMsg, setForecastMsg] = useState(null);
  // These must stay in the top hook block — declaring them after the early
  // return below violates react-hooks/rules-of-hooks and fails the CRA build.
  const [cameraMsg, setCameraMsg] = useState(null);
  const [roadworksMsg, setRoadworksMsg] = useState(null);
  const [roadNamesMsg, setRoadNamesMsg] = useState(null);
  const [uitleg, setUitleg] = useState(null);
  const [uitlegMsg, setUitlegMsg] = useState(null);
  const [fcd, setFcd] = useState(null);
  // Hoe oud de officiële gegevens werkelijk zijn. Zonder dit is een bron die
  // negen dagen niet draaide niet te onderscheiden van een die vanmorgen liep
  // — in allebei de gevallen staat er niets in het paneel.
  const [extern, setExtern] = useState(null);
  // Meldingen op één plek, ook de opgeruimde. Zie het blok onderaan.
  const [spotLat, setSpotLat] = useState("");
  const [spotLon, setSpotLon] = useState("");
  const [spotRows, setSpotRows] = useState(null);
  const [spotMsg, setSpotMsg] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const who = await axios.get(`${API}/admin/whoami`, adminConfig());
        if (!active || !who.data?.is_admin) return;
        setIsAdmin(true);
        const res = await axios.get(`${API}/admin/settings`, adminConfig());
        if (active) setValues(res.data?.values || {});
        // Het snelheidsbeeld in getallen. Best-effort: een backend die dit nog
        // niet kent, hoort het paneel niet leeg te laten.
        try {
          const f = await axios.get(`${API}/admin/fcd/status`, adminConfig());
          if (active) setFcd(f.data || null);
        } catch (e) { /* endpoint nog niet uitgerold */ }
        try {
          const x = await axios.get(`${API}/admin/external/status`, adminConfig());
          if (active) setExtern(x.data?.sources || null);
        } catch (e) { /* endpoint nog niet uitgerold */ }
      } catch (e) {
        // Not an admin or not in Telegram — panel stays hidden.
      }
    })();
    return () => { active = false; };
  }, []);

  if (!isAdmin || !values) return null;

  const set = (k, v) => { setValues(prev => ({ ...prev, [k]: v })); setSaved(false); };

  const lookUpSpot = async () => {
    const lat = parseFloat(String(spotLat).replace(",", "."));
    const lon = parseFloat(String(spotLon).replace(",", "."));
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      setSpotRows(null);
      setSpotMsg("Vul een breedte- en lengtegraad in, bijvoorbeeld 52.3523 en 4.7758.");
      return;
    }
    setSpotMsg("Zoeken…");
    try {
      const res = await axios.get(
        `${API}/admin/reports/at?lat=${lat}&lon=${lon}&radius_m=${SPOT_RADIUS_M}`,
        adminConfig(),
      );
      setSpotRows(res.data?.meldingen || []);
      setSpotMsg(
        res.data?.gevonden
          ? `${res.data.gevonden} gevonden, waarvan ${res.data.actief} actief.`
          : "Niets binnen deze straal.",
      );
    } catch (e) {
      setSpotRows(null);
      setSpotMsg("Kon niet ophalen. Is de backend al bijgewerkt?");
    }
  };

  const setTime = (hourKey, minKey, str) => {
    const [h, m] = (str || "0:0").split(":").map(n => parseInt(n, 10) || 0);
    setValues(prev => ({ ...prev, [hourKey]: h, [minKey]: m }));
    setSaved(false);
  };

  const setNum = (k, str, isFloat) => {
    const n = isFloat ? parseFloat(str) : parseInt(str, 10);
    set(k, isNaN(n) ? "" : n);
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = await axios.put(`${API}/admin/settings`, { values }, adminConfig());
      setValues(res.data?.values || values);
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const runForecast = async () => {
    setForecastMsg("Versturen…");
    try {
      await axios.post(`${API}/admin/forecast/run`, {}, adminConfig());
      setForecastMsg("✅ Verstuurd");
    } catch (e) {
      setForecastMsg("❌ Mislukt");
    }
    setTimeout(() => setForecastMsg(null), 3000);
  };

  // These two used to wait for the whole import and report a count. They now
  // only start it: a country's worth of cameras or road works takes minutes,
  // and the platform closes the connection long before that — which is why the
  // button used to say "Mislukt" while the import was still running happily.
  // Progress is read back from /admin/external/status.
  const runSync = async (source, setMsg, label) => {
    setMsg("Starten…");
    try {
      const res = await axios.post(`${API}/admin/external/${source}/run`, {}, adminConfig());
      setMsg(res.data?.status === "already_running" ? "Al bezig…" : `Gestart — ${label} komen binnen`);
    } catch (e) {
      setMsg("❌ Starten mislukt");
    }
    setTimeout(() => setMsg(null), 6000);
  };

  /* Unlike the two above this one finishes in a second — it is a primary-key
     read per way, not a geocoder call per report — so it waits for the answer
     and says what it did. */
  const runRoadNames = async () => {
    setRoadNamesMsg("Bezig…");
    try {
      const res = await axios.post(`${API}/admin/reports/roads/run`, {}, adminConfig());
      const { named = 0, remaining = 0 } = res.data || {};
      setRoadNamesMsg(
        named === 0 && remaining === 0
          ? "Niets te doen"
          : `${named} benoemd${remaining ? `, ${remaining} te gaan` : ""}`,
      );
    } catch (e) {
      setRoadNamesMsg("❌ Mislukt");
    }
    setTimeout(() => setRoadNamesMsg(null), 6000);
  };

  /* Of de database onnodig werk doet, in één getal.
   *
   * De vraag die dit beantwoordt: de kaart levert ongeveer 1.500 meldingen —
   * hoeveel documenten heeft Mongo moeten lézen om die te vinden? Is dat
   * ongeveer hetzelfde aantal, dan doet de index zijn werk. Is het een factor
   * meer, dan leest hij te breed en valt daar tijd te halen zonder dat er één
   * regel logica verandert.
   *
   * Dit bestond niet en was ook niet te beantwoorden: een
   * `explain("executionStats")` vraagt de connectiestring van de productie, en
   * die hoort nergens buiten de omgeving van de server. Dus voert de server hem
   * zelf uit en geeft alleen de getallen terug — zie
   * api/admin_routes.py:explain_map, en docs/performance-collaboration.md voor
   * waarom dit de volgende vraag was na het weghalen van de kwadratische lus.
   *
   * Op 75 km, want dat is het dure pad. De clients vragen 25 km. */
  const haalUitleg = async () => {
    setUitlegMsg("Meten…");
    setUitleg(null);
    try {
      const res = await axios.get(
        `${API}/admin/explain/map?radius_m=75000`, adminConfig());
      setUitleg(res.data);
      setUitlegMsg(null);
    } catch (e) {
      setUitlegMsg("❌ Mislukt");
      setTimeout(() => setUitlegMsg(null), 6000);
    }
  };

  /* Hoe oud de gegevens van één bron zijn.
   *
   * De woorden en de grens staan in syncAge.js, zodat ze te toetsen zijn —
   * dit is de regel die er niet was: op de live server droeg elke officiële
   * rij dezelfde `last_synced_at` van negen dagen oud, en nergens stond dat.
   * Een knop "Nu ophalen" zegt niets over wanneer dat voor het laatst
   * gebeurde. */
  const bronRegel = (naam) => {
    const bron = extern?.[naam];
    if (!bron) return null;
    const { tekst, oud, leeg } = syncAge(bron);
    return (
      <p className="admin-hint" style={oud ? { color: "#f87171" } : undefined}
         data-testid={`admin-extern-${naam}`}>
        {leeg ? tekst : `${bron.count.toLocaleString("nl-NL")} items · ${tekst}`}
        {oud && !bron.enabled ? " · de schakelaar staat uit" : ""}
      </p>
    );
  };

  const runCameras = () => runSync("cameras", setCameraMsg, "flitspalen");
  const runRoadworks = () => runSync("roadworks", setRoadworksMsg, "werkzaamheden");

  return (
    <div className="settings-section admin-section" data-testid="admin-section">
      <h4>🛠️ Beheer — Geplande berichten</h4>

      {/* Daily forecast */}
      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Dagelijkse forecast</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["scheduler.forecast_enabled"]}
              onChange={e => set("scheduler.forecast_enabled", e.target.checked)}
              data-testid="admin-forecast-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        {values["scheduler.forecast_enabled"] && (
          <div className="admin-sub">
            <span>Tijd</span>
            <input
              type="time"
              className="admin-time"
              value={hhmm(values["scheduler.forecast_hour"], values["scheduler.forecast_minute"])}
              onChange={e => setTime("scheduler.forecast_hour", "scheduler.forecast_minute", e.target.value)}
              data-testid="admin-forecast-time"
            />
          </div>
        )}
        <p className="admin-hint">Zet uit zolang er weinig data is.</p>
      </div>

      {/* Weekly summary */}
      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Wekelijks overzicht</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["scheduler.weekly_enabled"]}
              onChange={e => set("scheduler.weekly_enabled", e.target.checked)}
              data-testid="admin-weekly-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        {values["scheduler.weekly_enabled"] && (
          <div className="admin-sub">
            <select
              className="admin-select"
              value={values["scheduler.weekly_day"] || "sun"}
              onChange={e => set("scheduler.weekly_day", e.target.value)}
              data-testid="admin-weekly-day"
            >
              {WEEKDAYS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <input
              type="time"
              className="admin-time"
              value={hhmm(values["scheduler.weekly_hour"], values["scheduler.weekly_minute"])}
              onChange={e => setTime("scheduler.weekly_hour", "scheduler.weekly_minute", e.target.value)}
              data-testid="admin-weekly-time"
            />
          </div>
        )}
      </div>

      {/* External data sources */}
      <h4 style={{ marginTop: 4 }}>🛰️ Externe bronnen</h4>

      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Vaste flitspalen (OSM)</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["external.cameras_enabled"]}
              onChange={e => set("external.cameras_enabled", e.target.checked)}
              data-testid="admin-cameras-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        {values["external.cameras_enabled"] && (
          <div className="admin-sub">
            <span>Dagelijks om</span>
            <input
              type="time"
              className="admin-time"
              value={hhmm(values["external.cameras_hour"], values["external.cameras_minute"])}
              onChange={e => setTime("external.cameras_hour", "external.cameras_minute", e.target.value)}
              data-testid="admin-cameras-time"
            />
          </div>
        )}
        <p className="admin-hint">Officiële flitspalen uit OpenStreetMap. Getoond, geen bevestig-loop.</p>
        {bronRegel("cameras")}
        <button className="admin-secondary" style={{ marginTop: 8 }}
          onClick={runCameras} data-testid="admin-run-cameras">
          {cameraMsg || "Nu ophalen"}
        </button>
      </div>

      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Werkzaamheden (NDW)</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["external.roadworks_enabled"]}
              onChange={e => set("external.roadworks_enabled", e.target.checked)}
              data-testid="admin-roadworks-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        {values["external.roadworks_enabled"] && (
          <div className="admin-sub">
            <span>Dagelijks om</span>
            <input
              type="time"
              className="admin-time"
              value={hhmm(values["external.roadworks_hour"], values["external.roadworks_minute"])}
              onChange={e => setTime("external.roadworks_hour", "external.roadworks_minute", e.target.value)}
              data-testid="admin-roadworks-time"
            />
          </div>
        )}
        <p className="admin-hint">Actuele wegwerkzaamheden uit NDW. Toont het tijdvenster.</p>
        {bronRegel("roadworks")}
        <button className="admin-secondary" style={{ marginTop: 8 }}
          onClick={runRoadworks} data-testid="admin-run-roadworks">
          {roadworksMsg || "Nu ophalen"}
        </button>
      </div>

      <div className="admin-row">
        <span className="admin-label">Wegnamen van meldingen</span>
        <p className="admin-hint">
          Een melding zonder wegnaam kan nergens mee vergeleken worden en komt
          op het bord als "niet op je route" — ook als hij op jouw straat staat.
          Nieuwe meldingen bewaren hem zelf; dit haalt de oude in. Loopt ook
          elke 5 minuten vanzelf.
        </p>
        <button className="admin-secondary" style={{ marginTop: 8 }}
          onClick={runRoadNames} data-testid="admin-run-road-names">
          {roadNamesMsg || "Nu benoemen"}
        </button>
      </div>

      {/* Waarom een kaartverzoek duurt. Zie haalUitleg. */}
      <div className="admin-row">
        <span className="admin-label">Hoe hard de database werkt</span>
        <p className="admin-hint">
          De kaart levert ruim duizend meldingen. Dit meet hoeveel documenten
          Mongo daarvoor heeft moeten lézen. Ongeveer hetzelfde aantal is goed;
          een veelvoud betekent dat de index te breed leest en dat daar tijd te
          halen is zonder iets aan de app te veranderen.
        </p>
        <button className="admin-secondary" style={{ marginTop: 8 }}
          onClick={haalUitleg} data-testid="admin-explain-map">
          {uitlegMsg || "Meet de kaartquery"}
        </button>
        {uitleg && (
          <div data-testid="admin-explain-uitkomst">
            <div className="admin-nums" style={{ marginTop: 10 }}>
              <label>Geleverd
                <span className="admin-num-read">
                  {(uitleg.getallen?.nReturned ?? 0).toLocaleString("nl-NL")}
                </span>
              </label>
              <label>Gelezen
                <span className="admin-num-read">
                  {(uitleg.getallen?.totalDocsExamined ?? 0).toLocaleString("nl-NL")}
                </span>
              </label>
              <label>Per geleverd
                <span className="admin-num-read">
                  {uitleg.gelezen_per_geleverd ?? "—"}×
                </span>
              </label>
              <label>Tijd
                <span className="admin-num-read">
                  {uitleg.getallen?.executionTimeMillis ?? "—"} ms
                </span>
              </label>
            </div>
            {/* Het oordeel en de grens staan in leeslast.js, zodat ze te
                toetsen zijn. Zelfde afspraak als syncAge.js. */}
            <p className="admin-hint" style={
              leeslast(uitleg).teBreed ? { color: "#f87171" } : undefined
            }>
              {leeslast(uitleg).tekst}{indexRegel(uitleg)}
            </p>
            {/* Waaruit het weggegooide werk bestaat. Zie samenstelling() —
                dit bepaalt wélke reparatie werkt, en dat was niet te zien aan
                het tienvoud alleen. */}
            {uitleg.samenstelling && (
              <p className="admin-hint" data-testid="admin-explain-samenstelling">
                {samenstelling(uitleg)}
              </p>
            )}
            {vergelijking(uitleg) && (
              <p className="admin-hint" data-testid="admin-explain-vergelijking">
                {vergelijking(uitleg)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Wat het snelheidsbeeld intussen weegt.
          Er stond één open vraag voordat dit breed aan kan: hoeveel documenten
          levert één rit op. Die was door niemand te beantwoorden zonder toegang
          tot de database, en de hoofdcluster is 1 GB — dus staat het antwoord
          nu hier. Alleen totalen: geen wegvak, geen coördinaat, geen tijdstip
          fijner dan de emmer. */}
      {fcd && (
        <div className="admin-row">
          <span className="admin-label">Snelheidsbeeld (FCD)</span>
          <div className="admin-nums">
            <label>Emmers<span className="admin-num-read">{fcd.buckets}</span></label>
            <label>Wegvakken<span className="admin-num-read">{fcd.segments_covered}</span></label>
            <label>Profielvakken<span className="admin-num-read">{fcd.profile_cells}</span></label>
          </div>
          <p className="admin-hint">
            {fcd.buckets === 0
              ? "Nog niets gemeten. Er is een rit nodig met de rijmodus aan, en wegen moeten aanstaan — zonder wegvak is er niets om aan te hangen."
              : `Gemiddeld ${fcd.samples_per_bucket} metingen per emmer. Rond de 60 betekent één toestel per wegvak per minuut; hoger betekent dat er meer mensen over hetzelfde stuk rijden.`}
            {fcd.pending_rollup > 0 && ` ${fcd.pending_rollup} nog op te rollen.`}
            {!fcd.enabled && " Staat nu uit."}
          </p>
        </div>
      )}

      {/* ============================================================
          Wat er op één plek staat, ook wat opgeruimd is
          ============================================================
          Van de weg: "de dagen kloppen niet, deze hoort meer dan vijf dagen te
          staan." Twee heel verschillende oorzaken geven precies dat beeld — een
          verkeerd afgerond getal (dat was zo, en het is weg) of een eerdere
          melding op dezelfde plek waarvan het tellen opnieuw begon. Het tweede
          is alleen te zien als je ook naar opgeruimde meldingen kunt kijken.

          Hier en niet als losse URL: het endpoint vraagt om beheerdersrechten,
          en die stuurt een browser niet mee — de eerste poging leverde dan ook
          "Alleen voor beheerders" op. Dit is de plek waar dat bewijs al in de
          lucht hangt. */}
      <div className="admin-row">
        <span className="admin-label">Meldingen op een plek</span>
        <p className="admin-hint">
          Alles binnen {SPOT_RADIUS_M} m, inclusief wat niet meer actief is. Om
          "staat hier al N dagen" na te rekenen: kijk naar <b>created_at</b> en
          naar <b>spot_key</b> — twee documenten met een verschillende spot_key
          op dezelfde meter zijn twee camera's volgens de app en één volgens de
          weg, en dan begint het tellen opnieuw.
        </p>
        <div className="admin-nums">
          <label>Lat
            <input type="text" inputMode="decimal" className="admin-num"
              value={spotLat} onChange={e => setSpotLat(e.target.value)}
              data-testid="admin-spot-lat" />
          </label>
          <label>Lon
            <input type="text" inputMode="decimal" className="admin-num"
              value={spotLon} onChange={e => setSpotLon(e.target.value)}
              data-testid="admin-spot-lon" />
          </label>
        </div>
        <button className="admin-secondary" style={{ marginTop: 8 }}
          onClick={lookUpSpot} data-testid="admin-spot-btn">
          Zoek op deze plek
        </button>
        {spotMsg && <p className="admin-hint">{spotMsg}</p>}
        {spotRows && spotRows.map(r => (
          <div key={r.id} className={`admin-spot ${r.actief ? "" : "off"}`}>
            <div className="admin-spot-h">
              <b>{r.authority}</b>
              <span>{r.afstand_m} m</span>
              <em>{r.actief ? r.lifecycle_status : `${r.lifecycle_status} · opgeruimd`}</em>
            </div>
            <div className="admin-spot-b">
              <span>eerste melding <b>{(r.created_at || "?").slice(0, 16).replace("T", " ")}</b></span>
              <span>laatst bevestigd <b>{(r.last_confirmed_at || "—").slice(0, 16).replace("T", " ")}</b></span>
              <span>{r.mensen} mensen · {r.confirmations}× · {r.vrijgaven} vrijgaven</span>
              <span>dagen gezien: {r.confirm_days.length ? r.confirm_days.join(", ") : "—"}</span>
              <span className="admin-spot-key">spot_key {r.spot_key || "geen"} · seg {r.seg_id ?? "—"} {r.seg_dir || ""}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-row">
        <span className="admin-label">Officieel op het bord</span>
        <div className="admin-nums">
          <label>Flitspalen (m)
            <input type="number" min="50" step="50" className="admin-num"
              value={values["board.official_radius_m"] ?? ""}
              onChange={e => setNum("board.official_radius_m", e.target.value)}
              data-testid="admin-official-radius" />
          </label>
          <label>Werkzaamheden (m)
            <input type="number" min="50" step="50" className="admin-num"
              value={values["board.roadworks_radius_m"] ?? ""}
              onChange={e => setNum("board.roadworks_radius_m", e.target.value)}
              data-testid="admin-roadworks-radius" />
          </label>
        </div>
        <p className="admin-hint">
          Flitspalen geldt voor vaste flitsers, trajectcontroles en
          roodlichtcamera's — die worden bovendien alleen op je eigen weg en
          richting getoond, dus ruim mag: 3000 m geeft bij 100 km/u ongeveer
          twee minuten. Mobiele en telefoonflitsers vallen hier níét onder;
          die volgen de community-straal. Werkzaamheden op 200 m betekent dat
          je erop staat voordat je het ziet — 2000 m is bruikbaarder.
        </p>
      </div>

      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Alleen op mijn weg</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["board.match_road"]}
              onChange={e => set("board.match_road", e.target.checked)}
              data-testid="admin-match-road-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        <p className="admin-hint">Flitspalen en werkzaamheden alleen tonen als ze op dezelfde straat staan als de gebruiker. Zonder dit komt ook de parallelweg of het viaduct erboven op het bord.</p>
      </div>

      <div className="admin-row">
        <span className="admin-label">Officieel samenvoegen</span>
        <div className="admin-nums">
          <label>Werkzaamheden (m)
            <input type="number" min="0" step="50" className="admin-num"
              value={values["board.roadworks_cluster_meters"] ?? ""}
              onChange={e => setNum("board.roadworks_cluster_meters", e.target.value)}
              data-testid="admin-roadworks-cluster" />
          </label>
          <label>Flitspalen (m)
            <input type="number" min="0" step="10" className="admin-num"
              value={values["board.camera_cluster_meters"] ?? ""}
              onChange={e => setNum("board.camera_cluster_meters", e.target.value)}
              data-testid="admin-camera-cluster" />
          </label>
        </div>
        <p className="admin-hint">NDW splitst één project over meerdere adressen, en een portaal boven de snelweg heeft een camera per rijstrook. Binnen deze afstand wordt dat één kaart. 0 = uit.</p>
      </div>

      <div className="admin-row">
        <div className="admin-row-main">
          <span className="admin-label">Snelheidslimieten (OSM)</span>
          <label className="admin-switch">
            <input
              type="checkbox"
              checked={!!values["roads.enabled"]}
              onChange={e => set("roads.enabled", e.target.checked)}
              data-testid="admin-roads-toggle"
            />
            <span className="admin-slider" />
          </label>
        </div>
        <p className="admin-hint">
          Toont de limiet in de rijmodus. Zet dit pas aan nadat het wegennet is
          geïmporteerd — staat het uit, dan toont de app geen limiet.
        </p>
      </div>

      {/* Behaviour tuners */}
      <h4 style={{ marginTop: 4 }}>⚙️ Gedrag</h4>

      <div className="admin-row">
        <span className="admin-label">Melding-vervaltijd (min)</span>
        <div className="admin-nums">
          <label>1 melding
            <input type="number" min="1" className="admin-num"
              value={values["expiry.single_report_minutes"] ?? ""}
              onChange={e => setNum("expiry.single_report_minutes", e.target.value)}
              data-testid="admin-expiry-1" />
          </label>
          <label>2 meldingen
            <input type="number" min="1" className="admin-num"
              value={values["expiry.two_reports_minutes"] ?? ""}
              onChange={e => setNum("expiry.two_reports_minutes", e.target.value)}
              data-testid="admin-expiry-2" />
          </label>
          <label>3+ meldingen
            <input type="number" min="1" className="admin-num"
              value={values["expiry.three_plus_minutes"] ?? ""}
              onChange={e => setNum("expiry.three_plus_minutes", e.target.value)}
              data-testid="admin-expiry-3" />
          </label>
        </div>
      </div>

      <div className="admin-row">
        <span className="admin-label">Watch</span>
        <div className="admin-nums">
          <label>Standaard straal (km)
            <input type="number" min="0.5" step="0.5" className="admin-num"
              value={values["watch.default_radius_km"] ?? ""}
              onChange={e => setNum("watch.default_radius_km", e.target.value, true)}
              data-testid="admin-radius" />
          </label>
          <label>Nabijheid (m)
            <input type="number" min="50" step="50" className="admin-num"
              value={values["watch.proximity_threshold_meters"] ?? ""}
              onChange={e => setNum("watch.proximity_threshold_meters", e.target.value)}
              data-testid="admin-proximity" />
          </label>
        </div>
      </div>

      <div className="admin-row">
        <span className="admin-label">Live-Radar drempels (bevestigingen)</span>
        <div className="admin-nums">
          <label>Actief
            <input type="number" min="1" className="admin-num"
              value={values["confidence.active_control_threshold"] ?? ""}
              onChange={e => setNum("confidence.active_control_threshold", e.target.value)}
              data-testid="admin-active" />
          </label>
          <label>Hot
            <input type="number" min="1" className="admin-num"
              value={values["confidence.hot_control_threshold"] ?? ""}
              onChange={e => setNum("confidence.hot_control_threshold", e.target.value)}
              data-testid="admin-hot" />
          </label>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}

      <button className="admin-save" onClick={save} disabled={saving} data-testid="admin-save">
        {saved ? "✅ Opgeslagen" : saving ? "Opslaan…" : "Opslaan"}
      </button>

      <button className="admin-secondary" onClick={runForecast} data-testid="admin-run-forecast">
        {forecastMsg || "Forecast nu versturen"}
      </button>
    </div>
  );
};

// ============================================================
// ADMIN DIAGNOSE — temporary visible-in-UI debug helper.
// Renders a "🩺 Admin diagnose" button in Instellingen. On click, calls
// /api/admin/whoami with the Telegram initData and shows the raw JSON on
// screen so we can pinpoint exactly why the admin panel isn't appearing
// (missing env var, wrong bot token, id mismatch, etc.) without needing
// DevTools inside the Mini App.
// ============================================================
const AdminDiagnose = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const initData = (() => {
        try { return window.Telegram?.WebApp?.initData || ""; } catch (e) { return ""; }
      })();
      const tgUser = (() => {
        try { return window.Telegram?.WebApp?.initDataUnsafe?.user || null; } catch (e) { return null; }
      })();
      const res = await axios.get(`${API}/admin/whoami`, {
        headers: { "X-Telegram-Init-Data": initData },
      });
      setResult({
        client: {
          in_telegram: !!window.Telegram?.WebApp,
          init_data_length: initData.length,
          telegram_user_id: tgUser?.id ?? null,
          telegram_username: tgUser?.username ?? null,
        },
        server: res.data,
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Fout bij ophalen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section" data-testid="admin-diagnose">
      <h4>🩺 Admin diagnose</h4>
      <p className="description" style={{ fontSize: "0.85em", marginTop: 4 }}>
        Klik om te zien waarom de Beheer-sectie wel/niet verschijnt.
      </p>
      <button
        className="admin-secondary"
        onClick={run}
        disabled={loading}
        data-testid="admin-diagnose-run"
      >
        {loading ? "Bezig…" : "Diagnose uitvoeren"}
      </button>
      {error && (
        <pre
          style={{
            marginTop: 8,
            padding: 8,
            background: "#fee",
            border: "1px solid #f99",
            borderRadius: 6,
            fontSize: "0.75em",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          Error: {error}
        </pre>
      )}
      {result && (
        <pre
          data-testid="admin-diagnose-output"
          style={{
            marginTop: 8,
            padding: 8,
            background: "#f4f4f4",
            border: "1px solid #ccc",
            borderRadius: 6,
            fontSize: "0.75em",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: 320,
            overflow: "auto",
          }}
        >
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
};

// ============================================================
// SETTINGS MODAL
// ============================================================
const SettingsModal = ({
  onClose, userLocation, userId,
  watchEnabled, setWatchEnabled, radius, setRadius,
  selectedAuthorities, setSelectedAuthorities,
  profielen, voertuig, kiesVoertuig, voertuigExtra, kiesVoertuigExtra,
}) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>⚙️ Instellingen</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="settings-content">
          <AccountPanel />
          {/* Waar Watch heen is. Een scherm dat je één keer instelt hoort niet
              tussen de vier knoppen waar je elke dag op drukt — dezelfde
              verhuizing als op Android, waar het achter hetzelfde tandwiel
              zit. */}
          <WatchTab
            userLocation={userLocation}
            userId={userId}
            watchEnabled={watchEnabled}
            setWatchEnabled={setWatchEnabled}
            radius={radius}
            setRadius={setRadius}
            selectedAuthorities={selectedAuthorities}
            setSelectedAuthorities={setSelectedAuthorities}
          />

          <div className="settings-section">
            <h4>Over MeldRadar</h4>
            <p className="version">Versie 2.0</p>
            <p className="description">Community-gedreven verkeerscontrole alerts</p>
          </div>

          {/* Bovenaan, want deze keuze bepaalt wat alle andere schermen laten
              zien. Geen opslaan-knop: een tik is de keuze, en het bord is bij
              het sluiten al veranderd. */}
          <div className="settings-section">
            <h4>Wat rijd je?</h4>
            <p className="description">
              Bepaalt welke controles je ziet en kunt melden, en welke
              maximumsnelheid voor jou geldt.
            </p>
            <div className="voertuig-keuze" role="radiogroup" aria-label="Voertuig">
              {(profielen || []).map(p => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={p.id === voertuig}
                  className={`voertuig-optie ${p.id === voertuig ? "aan" : ""}`}
                  onClick={() => kiesVoertuig(p.id)}
                  data-testid={`voertuig-${p.id}`}
                >
                  <span className="voertuig-naam">{p.naam}</span>
                  <span className="voertuig-onder">{p.onder}</span>
                  {p.max_kmh != null && (
                    <span className="voertuig-cap">max {p.max_kmh} km/u</span>
                  )}
                </button>
              ))}
            </div>
            {(() => {
              // De losse vraag die bij sommige profielen hoort, omdat het
              // antwoord de limiet verandert en wij het niet kunnen afleiden.
              const gekozen = profielVan(profielen, voertuig);
              if (!gekozen || !gekozen.vraag) return null;
              const sleutel = gekozen.vraag.sleutel;
              const aan = !!(voertuigExtra || {})[sleutel];
              return (
                <label className="voertuig-vraag">
                  <input
                    type="checkbox"
                    checked={aan}
                    onChange={e => kiesVoertuigExtra({
                      ...(voertuigExtra || {}), [sleutel]: e.target.checked,
                    })}
                    data-testid={`voertuig-vraag-${sleutel}`}
                  />
                  <span>
                    <strong>{gekozen.vraag.vraag}</strong>
                    <span className="description">{gekozen.vraag.uitleg}</span>
                  </span>
                </label>
              );
            })()}
          </div>

          {/* Stated once, here, rather than three words under every camera on
              the board. ODbL asks for attribution; it does not ask for it on
              each row, and a licence named in full is more honest than "via
              OpenStreetMap" squeezed onto a card. */}
          <div className="settings-section">
            <h4>Bronnen</h4>
            <p className="description">
              Vaste flitsers, trajectcontroles en roodlichtcamera's komen uit{" "}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap
              </a>
              , beschikbaar onder de Open Database License (ODbL).
            </p>
            <p className="description">
              Wegwerkzaamheden komen uit het Nationaal Dataportaal Wegverkeer (NDW).
            </p>
            <p className="description">
              Alle overige meldingen komen van gebruikers.
            </p>
          </div>

          
          <div className="settings-section">
            <h4>Links</h4>
            <a href="https://t.me/meldradar_nl" target="_blank" rel="noopener noreferrer" className="settings-link">
              <span className="link-icon">📱</span>
              <span className="link-text">Telegram Groep</span>
              <span className="link-arrow">→</span>
            </a>
            <a href="https://t.me/verkeerscontrole_bot" target="_blank" rel="noopener noreferrer" className="settings-link">
              <span className="link-icon">🤖</span>
              <span className="link-text">Telegram Bot</span>
              <span className="link-arrow">→</span>
            </a>
          </div>

          <AdminDiagnose />
          <AdminSection />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN MINIAPP COMPONENT
// ============================================================
// Helper to get Telegram WebApp user ID
const getTelegramUserId = () => {
  try {
    if (window.Telegram?.WebApp?.initDataUnsafe?.user?.id) {
      return window.Telegram.WebApp.initDataUnsafe.user.id;
    }
    let storedId = localStorage.getItem('meldradar_user_id');
    if (!storedId) {
      storedId = Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 1000000);
      localStorage.setItem('meldradar_user_id', storedId.toString());
    }
    return parseInt(storedId);
  } catch (e) {
    return Math.floor(Date.now() / 1000);
  }
};

// Helper to get Telegram WebApp username
const getTelegramUsername = () => {
  try {
    const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
    if (user) {
      return user.username || user.first_name || 'Anoniem';
    }
    return 'Anoniem';
  } catch (e) {
    return 'Anoniem';
  }
};

function MiniApp() {
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTER, cities: [], sort: DEFAULT_SORT,
  });
  const [activeTab, setActiveTab] = useState("meldingen");
  const [showSettings, setShowSettings] = useState(false);
  const [meldingen, setMeldingen] = useState([]);
  // Street the user is on. The board is scoped to it and says so in its
  // header; null means we couldn't determine it and nothing is scoped.
  const [currentRoad, setCurrentRoad] = useState(null);
  const [currentHectometer, setCurrentHectometer] = useState(null);
  // One sentence about this spot's history at this hour, or null when there
  // is nothing worth saying. Context, not a report.
  const [hunch, setHunch] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const hotspotTimeWindow = '30d';
  const [stats, setStats] = useState({ active: 0, total: 0, reporters: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [groupLeaderboard, setGroupLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  // Whether the location question is settled enough to load on: answered with
  // a fix, answered with a refusal, asked of a browser that has none — or
  // simply taken too long to keep a driver staring at nothing. Not the same as
  // "we have a location", and that difference is the whole point: before this,
  // "not yet asked" and "asked, no answer" both looked like `userLocation ===
  // null`, so the board was fetched without one while the GPS was still
  // thinking. See the fetch effect below.
  const [locationSettled, setLocationSettled] = useState(false);
  // Direction of travel while reporting, when the device can tell us. Lets the
  // backend keep a control on this carriageway apart from one on the opposite
  // side. Stays null when standing still or on the fallback location — never
  // guessed, because a wrong direction is worse than none.
  const [userHeading, setUserHeading] = useState(null);
  // How far off the fix might be, in metres. Sent with a report so the backend
  // can weigh it: a 5 m fix and a 400 m one used to count exactly the same.

  // Selected report for map view navigation
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Watch settings - lifted up to persist across tab switches
  const [watchEnabled, setWatchEnabled] = useState(false);
  const [watchRadius, setWatchRadius] = useState(3000);
  const [watchAuthorities, setWatchAuthorities] = useState([]);
  
  // Navigate to map and focus on a specific report
  // Tapping a report opens everything known about it. The map is still one
  // tap away from inside that screen, so nothing became harder to reach.
  const [detailReport, setDetailReport] = useState(null);
  const handleReportClick = useCallback((report) => {
    setDetailReport(report);
  }, []);
  
  // Clear selected report when leaving map
  const handleTabChange = useCallback((tabId) => {
    if (tabId !== "kaart") {
      setSelectedReport(null);
    }
    setActiveTab(tabId);
  }, []);
  
  // Get consistent user ID (from Telegram or localStorage)
  const telegramUserId = useMemo(() => getTelegramUserId(), []);
  const telegramUsername = useMemo(() => getTelegramUsername(), []);

  // Initialise Telegram WebApp (fixes grey screen when opened via bot)
  useEffect(() => {
    const tg = typeof window !== "undefined" ? window.Telegram?.WebApp : null;
    if (tg) {
      try {
        tg.ready();
        tg.expand();
        if (tg.setHeaderColor) tg.setHeaderColor("#020617");
        if (tg.setBackgroundColor) tg.setBackgroundColor("#020617");
      } catch (err) {
        console.warn("Telegram WebApp init failed:", err);
      }
    }
  }, []);

  // Get user location
  //
  // Asked for properly, which it was not: with no options at all the browser
  // is free to answer from Wi-Fi or cell towers — routinely hundreds of metres
  // out — and to hand back a fix it took minutes ago. That position was then
  // submitted as the location of a control. `enableHighAccuracy` asks for the
  // GPS, and `maximumAge` puts a ceiling on how stale an answer may be.
  //
  // The accuracy itself is now kept and sent along. The backend uses it to
  // decide whether the report may be placed on a road at all: a fix that is
  // 400 m uncertain should not be snapped to a street, and until now nothing
  // recorded that it was uncertain in the first place.
  useEffect(() => {
    if (!navigator.geolocation) {
      setUserLocation([52.3676, 4.9041]);
      setLocationSettled(true);
      return undefined;
    }

    // watchPosition en niet getCurrentPosition, en dat is de reparatie.
    //
    // Hier stond één enkele meting bij het openen. Gevolg: je positie bevroor
    // op de plek waar je de app opende, en `coords.heading` — die per
    // definitie null is als je stilstaat, en stilstaan is precies wat je doet
    // als je een app opent — bleef de hele sessie null. Zonder koers geeft de
    // server álle rijbanen terug, dus stonden er op de A10 richting noord twee
    // trajectcontroles van de zúidbaan als "op mijn route" op het scherm.
    // Nagemeten op de echte server: mét heading=0 vallen ze allebei weg.
    //
    // Zie driveFix.js voor waarom er twee snelheden zijn: elke fix voor de
    // trajectmodule, en een grover anker waarop bord en kaart hun verzoeken
    // doen. Zonder dat onderscheid was elke meter een netwerkverzoek.
    let anker = null;
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const c = position.coords;
        const fix = nieuweFix({
          lat: c.latitude,
          lon: c.longitude,
          heading: c.heading,
          speedMs: c.speed,
          accuracyM: c.accuracy,
          nowMs: position.timestamp || Date.now(),
        });
        setLocationSettled(true);
        if (!verdientAnker(anker, fix)) return;
        anker = fix;
        setUserLocation([fix.lat, fix.lon]);
        setUserHeading(fix.heading);
      },
      (error) => {
        console.log('Location error:', error);
        setUserLocation((was) => was || [52.3676, 4.9041]);
        // De terugval draagt geen richting en geen nauwkeurigheid die die naam
        // verdient — dit is de Dam, niet waar iemand is. En hij overschrijft
        // een positie die we al hadden niet: een enkele mislukte peiling is
        // geen reden om iemand naar Amsterdam te verplaatsen.
        setLocationSettled(true);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // How long we are willing to stare at nothing while the GPS makes up its
  // mind. `timeout` above allows fifteen seconds, and waiting all of them
  // before drawing anything would trade one bad screen for a worse one.
  //
  // After this the board loads without a location: community reports only, no
  // road name — honest, and immediately replaced the moment a fix arrives,
  // because that later fetch takes a higher ticket and wins by construction.
  // A phone with a recent fix answers in milliseconds, so this almost never
  // fires; it exists for the cold start and the refusal that never comes.
  useEffect(() => {
    const id = setTimeout(() => setLocationSettled(true), 2500);
    return () => clearTimeout(id);
  }, []);

  // Two requests were in flight on every open — one fired before the GPS had
  // answered, one after — and whichever came back last won. Measured against
  // the live server they finish 0.42 s and 0.48 s apart, so it was a coin
  // toss: refresh and the cameras are there, refresh and they are gone,
  // refresh and they are back. The gate below removes that first request;
  // this makes sure any other overlap — a poll landing during a location
  // change, a slow response — can never put an older answer on the screen.
  const claimFetch = useRef(latestOnly()).current;

  // Everything that is active, for both maps on this app — the full one and
  // the glance-sized one above the board. One request, one moment in time.
  const mapPins = useMapPins(userLocation);

  // ------------------------------------------------------------
  // Het voertuigprofiel
  // ------------------------------------------------------------
  // Eén keel, en met opzet hier. Bord, kaart, rit en de waarschuwingen lezen
  // allemaal uit `meldingen` en `mapPins`; filteren we daar, dan kan geen
  // enkel scherm per ongeluk ongefilterd blijven. Filteren in `matches()` van
  // boardFilter zou de tellingen in de filterbalk laten liegen, want die
  // rekenen over de onbewerkte lijst.
  //
  // Wisselen werkt meteen: `voertuig` is gewone state, dus een andere keuze
  // hertekent het bord zonder herstart.
  const { types: soorten, profielen } = useConfig();
  const [voertuig, setVoertuig] = useState(leesVoertuig);
  const [voertuigExtra, setVoertuigExtra] = useState(leesVoertuigExtra);

  const kiesVoertuig = useCallback((id) => {
    schrijfVoertuig(id);
    setVoertuig(id);
  }, []);
  const kiesVoertuigExtra = useCallback((extra) => {
    schrijfVoertuigExtra(extra);
    setVoertuigExtra(extra);
  }, []);

  const mijnMeldingen = useMemo(
    () => (meldingen || []).filter(m => gaatMijAan(m, soorten, profielen, voertuig)),
    [meldingen, soorten, profielen, voertuig]);
  const mijnPins = useMemo(
    () => (mapPins == null ? null
          : mapPins.filter(m => gaatMijAan(m, soorten, profielen, voertuig))),
    [mapPins, soorten, profielen, voertuig]);

  // Hoeveel er wegvalt, zodat een leeg scherm kan zeggen dat er wél iets
  // staat. Zonder dit leest "geen meldingen" als "de weg is vrij", en dat is
  // dezelfde onwaarheid die de dekkingsregel op het ritscherm voorkomt.
  const nietVoorMij = useMemo(() => {
    const bron = mapPins ?? meldingen ?? [];
    return bron.length - (mapPins ? (mijnPins || []).length : mijnMeldingen.length);
  }, [mapPins, meldingen, mijnPins, mijnMeldingen]);

  // Fetch data
  const fetchData = useCallback(async () => {
    const stale = claimFetch();
    try {
      // Pass location so official (external) items are limited to a radius
      // around the user — without it the board returns community only.
      //
      // And the course, when the device knows it. That is what turns the board
      // from "everything around you" into "what is coming up": the server
      // marks each item ahead or not from it, and the list below hides the
      // ones that are not. Without it every single item comes back marked
      // ahead, because there is no direction to judge against — which is why
      // the screen was full of things on other roads.
      //
      // Usually null, and honestly so: coords.heading needs actual movement,
      // so a phone held still has no course to give. The radius is what scopes
      // the board then.
      const board = userLocation
        ? [`lat=${userLocation[0]}`, `lon=${userLocation[1]}`]
        : [];
      if (userLocation && userHeading !== null) board.push(`heading=${userHeading}`);
      const meldingenUrl = board.length ? `${API}/board?${board.join("&")}` : `${API}/board`;
      /* Alleen het bord staat in het kritieke pad.
         ------------------------------------------------------------------
         Hier stonden vijf aanvragen in één `Promise.all`. Twee dingen mis
         daarmee, en alleen het tweede was zichtbaar zonder meten.

         De staart. Gemeten op 11 september tegen productie: het bord doet p50
         0,63 s, maar /api/leaderboard had p95 6,02 s. Met één `Promise.all`
         wacht een bord van een halve seconde dus zes seconden op een
         ranglijst. De mediaan van de vier extra bronnen is geen probleem
         (≤ 0,44 s); hun staart wel.

         En de gedeelde `catch`. Eén mislukte ranglijst gooide een geslaagd
         bord weg. Dat is nooit de bedoeling geweest.

         Het bord wordt nu meteen verwerkt zodra het er is. De vier andere
         bronnen hebben hun eigen klok (zie het blok hieronder) en hun eigen
         fouten — een omgevallen hotspot raakt de meldingen niet meer. */
      const meldingenRes = await axios.get(meldingenUrl);
      
      if (stale()) return;

      setMeldingen(meldingenRes.data.meldingen || []);
      setCurrentRoad(meldingenRes.data.road || null);
      setCurrentHectometer(meldingenRes.data.hectometer || null);
      setHunch(meldingenRes.data.hunch || null);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      // A superseded fetch does not get to say the screen has finished
      // loading either — the one that replaced it is still busy.
      if (!stale()) setLoading(false);
    }
  }, [userLocation, userHeading, claimFetch]);

  useEffect(() => {
    // Nothing until we know whether we have a location. Without one the board
    // returns community reports only and no road name, which is a legitimate
    // answer for somebody who refuses location — and completely wrong as a
    // first guess for somebody whose GPS simply had not replied yet. That
    // first request is where the flickering came from: it went out on every
    // open, and the real one that followed a moment later did not always win.
    if (!locationSettled) return;
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, locationSettled]);

  /* De vier bronnen die níét over de volgende kilometer gaan.
     ------------------------------------------------------------------
     Ze hingen aan hetzelfde positieanker als het bord: 150 meter, dus bij
     100 km/u elke 5,4 seconden opnieuw. Landelijke statistieken, twee
     ranglijsten en dertig dagen patroongeschiedenis, zeshonderdzesenzestig
     keer per uur opgehaald. Samen kostten ze vier keer zoveel verzoeken als
     het bord zelf, voor gegevens die per dag veranderen.

     Nu ieder hun eigen klok — zie ophaalTempo.js, waar de drempels staan en
     waar de rekensom een test is. Van 2.664 verzoeken per uur naar 78.

     En ieder hun eigen `catch`: een omgevallen ranglijst mag de meldingen
     niet raken. Dat was de tweede fout van de oude `Promise.all`. */
  const gehaaldExtra = useRef({});
  useEffect(() => {
    if (!locationSettled) return;
    let gestopt = false;

    const pak = async (naam, url, zet) => {
      const nu = {
        lat: userLocation ? userLocation[0] : null,
        lon: userLocation ? userLocation[1] : null,
        atMs: Date.now(),
      };
      if (!moetOphalen(gehaaldExtra.current[naam], nu, TEMPO[naam === "patronen" ? "patronen"
        : naam === "stats" ? "stats" : "ranglijst"])) return;
      try {
        const res = await axios.get(url());
        if (gestopt) return;
        gehaaldExtra.current[naam] = nu;
        zet(res.data);
      } catch (e) {
        // Stil, en met opzet. Dit zijn de blokken die het scherm sieren; een
        // ranglijst die het niet doet is geen reden om iets te melden, en al
        // helemaal geen reden om de meldingen weg te gooien.
      }
    };

    const patroonUrl = () => {
      const q = new URLSearchParams({ time_window: '30d', limit: '50' });
      if (userLocation) {
        q.set('lat', userLocation[0]);
        q.set('lon', userLocation[1]);
        q.set('radius_km', '35');
      }
      return `${API}/intelligence/hotspots?${q.toString()}`;
    };

    const ronde = () => {
      pak("stats", () => `${API}/stats`, (d) => setStats({
        active: d.active_meldingen || 0,
        total: d.total_meldingen || 0,
        reporters: d.total_users || 0,
      }));
      pak("leaderboard", () => `${API}/leaderboard`, setLeaderboard);
      pak("groepen", () => `${API}/groups/leaderboard`, setGroupLeaderboard);
      pak("patronen", patroonUrl, (d) => setHotspots(d.hotspots || []));
    };

    ronde();
    // Elke halve minuut kijken óf er iets mag; de drempels beslissen.
    const t = setInterval(ronde, 30000);
    return () => { gestopt = true; clearInterval(t); };
  }, [locationSettled, userLocation, hotspotTimeWindow]);

  // Geometric marks rather than emoji, and the bar sits at the bottom — both
  // straight from the v3 mockup. Emoji render differently on every phone and
  // drag a cartoon register into a screen that is otherwise about facts.
  //
  // Vijf knoppen sinds het Rit-tabblad erbij kwam. Dat is niet gratis — hier
  // stond dat er juist naar vier was teruggebracht zodat de knoppen breder
  // werden — en het staat er toch, om dezelfde reden als op Android: een rit
  // plannen doe je stilstaand vóór je vertrekt, en daarna de hele rit niet
  // meer. Op 360 px wordt een kolom daarmee ~68 px bij een label van 9,5 px;
  // "Rit" past, een langere naam zou het niet doen.
  //
  // Let op: het aantal kolommen staat óók in MiniApp.css (.nav-tabs,
  // grid-template-columns). Die twee horen gelijk te zijn, en als je er hier
  // een bijzet en daar niet, valt de vijfde knop stil naar een tweede rij.
  const tabs = [
    { id: "meldingen", glyph: "◎", name: "Bord" },
    { id: "kaart", glyph: "▤", name: "Kaart" },
    // Derde, tussen Kaart en Feed, net als op Android. Bord -> Kaart -> Rit is
    // een gedachte die oploopt: wat staat er nu, waar staat het, wat kom ik
    // tegen. Feed en Regio zijn terugkijken en horen daarachter.
    { id: "rit", glyph: "▷", name: "Rit" },
    // Watch was een instellingenscherm dat een van de vier brede knoppen
    // bezette. Het staat nu achter het tandwiel op de Feed, precies zoals op
    // Android — een instellingenscherm is geen tabblad waard, en wat er wél
    // elke dag verandert had er geen.
    { id: "feed", glyph: "◈", name: "Feed" },
    { id: "regio", glyph: "◯", name: "Regio" },
  ];

  return (
    <div className="miniapp-container" data-testid="miniapp-container">
      {/* No bar with the logo and the app's name here any more. Telegram
          already draws one directly above ("Meldradar bot NL · mini-app"), so
          this one was a second header saying the same thing and costing the
          board sixty pixels. The settings button moved into the screen's own
          header row, the way the mockup has it. */}

      {/* Tab Content */}
      <main className="tab-container">
        {activeTab === "meldingen" && (
          <MeldingenTab
            filters={filters} setFilters={setFilters}
            meldingen={mijnMeldingen}
            mapPins={mijnPins}
            nietVoorMij={nietVoorMij}
            voertuig={voertuig}
            voertuigExtra={voertuigExtra}
            hotspots={hotspots}
            currentRoad={currentRoad}
            currentHectometer={currentHectometer}
            hunch={hunch}
            onOpenMap={() => setActiveTab("kaart")}
            loading={loading}
            onReportClick={handleReportClick}
            userLocation={userLocation}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
        {activeTab === "melden" && (
          <MeldenTab
            onSubmit={fetchData}
            userId={telegramUserId}
            username={telegramUsername}
            voertuig={voertuig}
          />
        )}
        {activeTab === "kaart" && (
          <KaartTab 
            filters={filters} setFilters={setFilters}
            meldingen={mijnMeldingen}
            mapPins={mijnPins}
            hotspots={hotspots}
            userLocation={userLocation}
            loading={loading}
            selectedReport={selectedReport}
            onReportClick={handleReportClick}
          />
        )}
        {activeTab === "rit" && (
          /* Alleen de positie, en met opzet geen userId: wat je niet hebt,
             plak je ook niet per ongeluk bij een routeverzoek. Zie de kop van
             RitTab. */
          <RitTab userLocation={userLocation} />
        )}
        {activeTab === "feed" && (
          <FeedTab
            userLocation={userLocation}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
        {activeTab === "regio" && (
          <MijnRegioTab 
            stats={stats}
            hotspots={hotspots}
            leaderboard={leaderboard}
            groupLeaderboard={groupLeaderboard}
            hotspotTimeWindow={hotspotTimeWindow}
          />
        )}
      </main>

      {/* Reporting is what the whole app runs on — no reports, no board, no
          pattern, nothing to warn anyone about — and it used to be a tab the
          same size as "Regio". It is a button now, full width, right where the
          thumb already is. Part of the shell rather than the board, so it is
          reachable from every tab; hidden on the reporting screen itself,
          where it would only point at what you are already looking at. */}
      {activeTab !== "melden" && (
        <button
          className="meld-btn"
          onClick={() => handleTabChange("melden")}
          data-testid="meld-btn"
        >
          <span className="meld-plus" aria-hidden="true">⊕</span> Melding maken
        </button>
      )}

      {/* Navigation at the bottom, where a thumb reaches it. It used to sit
          under the header, which put five buttons between you and the first
          report on every screen. Four now that Melden has its own button, so
          each one is wider and easier to hit. */}
      <nav className="nav-tabs" data-testid="nav-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            <span className="tab-glyph" aria-hidden="true">{tab.glyph}</span>
            <span className="tab-name">{tab.name}</span>
          </button>
        ))}
      </nav>

      {/* Everything known about one report — opened by tapping it, closed by
          the arrow. A full surface rather than a sheet, because it holds a
          heatmap and is read while stationary. */}
      {detailReport && (
        <IntelligenceDetail
          report={detailReport}
          userId={telegramUserId}
          distanceKm={
            // The server's measurement first, for the same reason the list
            // uses it: it knows the road, this does not.
            detailReport.distance_m != null
              ? detailReport.distance_m / 1000
              : (userLocation && detailReport.lat != null
                  ? haversineKm(userLocation[0], userLocation[1], detailReport.lat, detailReport.lon)
                  : null)
          }
          onClose={() => setDetailReport(null)}
          onChanged={fetchData}
        />
      )}

      {/* "Was hij er nog?" hoort niet bij één tabblad. Zie ProximityWatch. */}
      <ProximityWatch
        userLocation={userLocation}
        userId={telegramUserId}
        watchEnabled={watchEnabled}
        radius={watchRadius}
      />

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
          profielen={profielen}
          voertuig={voertuig}
          kiesVoertuig={kiesVoertuig}
          voertuigExtra={voertuigExtra}
          kiesVoertuigExtra={kiesVoertuigExtra}
          onClose={() => setShowSettings(false)}
          userLocation={userLocation}
          userId={telegramUserId}
          watchEnabled={watchEnabled}
          setWatchEnabled={setWatchEnabled}
          radius={watchRadius}
          setRadius={setWatchRadius}
          selectedAuthorities={watchAuthorities}
          setSelectedAuthorities={setWatchAuthorities}
        />
      )}
    </div>
  );
}

// Wrap MiniApp with ConfigProvider
const MiniAppWithConfig = () => (
  <ConfigProvider>
    <MiniApp />
  </ConfigProvider>
);

export default MiniAppWithConfig;




